import { openSafeExternalUrl, safeExternalUrl } from '../../utils/safeNavigation';
import { friendlyError } from '../../utils/userMessages';
import { brandPalette as palette, getContrastText, getReadableColor, getGradientContrastText } from '../../utils/brandPalette';
import { getCompanyCoverUrl, getCompanyLogoUrl, getPersonPhotoUrl } from '../../utils/companyIdentity';
// src/components/dashboard/CompanyDashboard.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  TelcoFair · Company Command Deck
//  Aesthetic: "Warm Liquid Glass" — ivory canvas, molten-orange accents,
//  frosted floating panes with specular rim-light and pointer-reactive sheen.
//  Layout: pure CSS Grid only (no MUI <Grid>) to avoid grid-in-grid collisions.
//
//  AI-INTRO GATING:
//  When Company A sends an AI intro to Company B, an `invitations` doc is
//  created (companyId: A, targetCompanyId: B, status: 'pending'). Until B
//  accepts it, A's "Companies on Platform" listing shows B's card as
//  dimmed/"Pending", and Message / Schedule Meeting stay locked in B's
//  detail dialog. Once the doc's status flips to 'accepted' (handled by B's
//  own notification accept flow), the card unlocks in real time via the
//  `invitations` onSnapshot listener below (see `sentIntros`).
//
//  STATS: the stat strip pulls from meetings (total/completed/pending +
//  received "meeting invites"), invitations (connections + "new invitations"
//  received), a one-time platform-wide user count, and an `introductions`
//  listener for accepted person-to-person connections ("Users Connected").
// ─────────────────────────────────────────────────────────────────────────────

import React, {
    useState,
    useEffect,
    useRef,
    useMemo,
    useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    Avatar,
    Chip,
    IconButton,
    Skeleton,
    Badge,
    Dialog,
    DialogContent,
    DialogActions,
    TextField,
    Fab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Autocomplete,
    LinearProgress,
    Tooltip,
    Fade,
    Card,
    CardContent,
    Tabs,
    Tab,
    Collapse,
} from '@mui/material';
import {
    Check as CheckIcon,
    Link as LinkIcon,
    Event as EventIcon,
    People as PeopleIcon,
    Business as BusinessIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Refresh as RefreshIcon,
    Add as AddIcon,
    CalendarToday as CalendarIcon,
    Schedule as ScheduleIcon,
    ArrowForward as ArrowForwardIcon,
    FiberManualRecord as LiveIcon,
    Analytics as AnalyticsIcon,
    NotificationsActive as NotificationIcon,
    RocketLaunch as RocketIcon,
    Close as CloseIcon,
    Storefront as StorefrontIcon,
    EmojiEvents as TrophyIcon,
    Search as SearchIcon,
    Videocam as VideocamIcon,
    SupportAgent as SupportAgentIcon,
    Send as SendIcon,
    Group as GroupIcon,
    Timer as TimerIcon,
    Warning as WarningIcon,
    Delete as DeleteIcon,
    ImageOutlined as ImageOutlinedIcon,
    TaskAlt as TaskAltIcon,
    PaidOutlined as PaidIcon,
    Message as MessageIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Verified as VerifiedIcon,
    Chat as ChatIcon,
    Visibility as VisibilityIcon,
    AutoAwesome as AiIcon,
    AdsClick as AdIcon,
    Diamond as DiamondIcon,
    Stars as StarsIcon,
    LocationOn as LocationIcon,
    Public as PublicIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Language as LanguageIcon,
    LinkedIn as LinkedInIcon,
    Handshake as HandshakeIcon,
    ArrowUpward as ArrowUpIcon,
    GraphicEq as SignalIcon,
    Speed as BoltIcon,
    Dashboard as GridViewIcon,
    FiberManualRecord as CircleIcon,
    AutoAwesome as AutoAwesomeIcon,
    Menu as MoreVertIcon,
    Schedule as UpcomingIcon,
    Restore as HistoryIcon,
    Email as InboxIcon,
    Assignment as RequestPageIcon,
    Pending as PendingIcon,
    Lock as LockIcon,
    Pending as HourglassTopIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../components/auth/AuthContext';
import Header from '../common/Header/Header';
import { db } from '../../config/firebase';
import {
    collection,
    query,
    where,
    onSnapshot,
    doc,
    addDoc,
    updateDoc,
    serverTimestamp,
    getCountFromServer,
    limit,
    or,
    getDoc,
    getDocs,
    orderBy,
    Timestamp,
    documentId,
    increment,
} from 'firebase/firestore';
import toast from 'react-hot-toast';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    ChartTooltip,
    Legend,
    Filler
);

/* ═══════════════════════════════════════════════════════════════════════════
   1 · DESIGN TOKENS
   ═══════════════════════════════════════════════════════════════════════════ */



const ACCENTS = [
    palette.rust,
    palette.teal,
    palette.amber,
    palette.pine,
    palette.plum,
    palette.indigo,
    palette.rose,
    palette.coral,
    palette.mint,
    palette.sky,
];

const GRADIENT_PAIRS = [
    ['#C2521F', '#F28B4C'],
    ['#0F6B76', '#3FAAB5'],
    ['#B07A18', '#EDB43F'],
    ['#1E5740', '#5A9B7C'],
    ['#5C2170', '#A25ABE'],
    ['#243A9E', '#6C82E8'],
    ['#E85D75', '#FF8A9B'],
    ['#FF6B4A', '#FF9A7A'],
    ['#34D399', '#6EE7B7'],
    ['#38BDF8', '#7DD3FC'],
];

const COMPANY_TYPE_COLORS = {
    Operator: palette.rust,
    Vendor: palette.plum,
    Aggregator: palette.teal,
    'Solution Provider': palette.pine,
    Consultant: palette.amber,
    ISP: palette.indigo,
    Telecom: palette.rustGlow,
    Technology: '#3B4CC0',
    'Service Provider': palette.tealGlow,
    Equipment: palette.pineGlow,
    Software: palette.plumGlow,
    Network: '#4A7DE8',
    Wholesale: palette.success,
    Retail: palette.warning,
    'Cloud Provider': '#5B62D6',
    'Data Center': '#8F3A12',
    Security: palette.danger,
    Other: '#7A6B5C',
};

const fontDisplay = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontBody = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontMono = "'JetBrains Mono', 'SFMono-Regular', monospace";

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,500&family=Instrument+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap');`;

/* ── colour utilities ────────────────────────────────────────────────────── */

const hexToRgb = (hex) => {
    if (typeof hex !== 'string') return { r: 22, g: 18, b: 16 };
    let h = hex.replace('#', '').trim();
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return { r: 22, g: 18, b: 16 };
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
};

const rgba = (color, a = 1) => {
    if (typeof color === 'string' && color.startsWith('rgb')) return color;
    const { r, g, b } = hexToRgb(color);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

/* ── liquid-glass surface recipe ────────────────────────────────────────── */

const liquid = (accent = palette.rust, { depth = 1, radius = 16 } = {}) => ({
    position: 'relative',
    borderRadius: `${radius}px`,
    background: `linear-gradient(145deg, rgba(255,255,255,.98), ${rgba(accent, 0.025)})`,
    backdropFilter: 'blur(16px) saturate(130%)',
    WebkitBackdropFilter: 'blur(16px) saturate(130%)',
    border: `1px solid ${rgba(accent, 0.11)}`,
    boxShadow: depth >= 2
        ? `0 14px 34px ${rgba(accent, 0.10)}, 0 4px 12px rgba(25,57,78,.055)`
        : '0 8px 22px rgba(25,57,78,.07), 0 2px 7px rgba(25,57,78,.035)',
    overflow: 'hidden',
    transition: 'transform .22s ease, box-shadow .22s ease, border-color .22s ease',
    '&::before': {
        content: '""',
        position: 'absolute',
        right: -44,
        bottom: -56,
        width: 160,
        height: 160,
        borderRadius: '50%',
        background: `radial-gradient(circle at 35% 35%, ${rgba(accent, 0.14)}, transparent 68%)`,
        pointerEvents: 'none',
    },
});

const useSheen = () => {
    const ref = useRef(null);
    const onPointerMove = useCallback((e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
        el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
        el.style.setProperty('--sheen', '1');
    }, []);
    const onPointerLeave = useCallback(() => {
        const el = ref.current;
        if (el) el.style.setProperty('--sheen', '0');
    }, []);
    return { ref, onPointerMove, onPointerLeave };
};

const sheenLayer = () => ({
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 0,
});

/* ── glass well (recessed) ──────────────────────────────────────────────── */

const glassWell = (radius = '10px', accent = null) => ({
    borderRadius: radius,
    background: accent ? rgba(accent, 0.055) : '#F4F8FB',
    border: `1px solid ${accent ? rgba(accent, 0.11) : rgba(palette.ink, 0.06)}`,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,.75)',
});

/* ═══════════════════════════════════════════════════════════════════════════
   2 · FORMAT / MATH HELPERS
   ═══════════════════════════════════════════════════════════════════════════ */

const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value?.toDate === 'function') {
        try { return value.toDate(); } catch { return null; }
    }
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
};

const formatRelative = (value) => {
    const d = toDate(value);
    if (!d) return '—';
    const diff = Date.now() - d.getTime();
    if (diff < 0) return 'Scheduled';
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'Just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
};

const formatDateTime = (value) => {
    const d = toDate(value);
    if (!d) return 'Date TBD';
    return d.toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    });
};

const compact = (n) => {
    const v = Number(n) || 0;
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 10_000) return `${(v / 1000).toFixed(1)}K`;
    return Math.round(v).toLocaleString();
};

/* rAF count-up */
const useCountUp = (target, duration = 900) => {
    const [display, setDisplay] = useState(0);
    const fromRef = useRef(0);

    useEffect(() => {
        const to = Number(target) || 0;
        const from = fromRef.current;
        if (from === to) { setDisplay(to); return undefined; }

        let raf;
        const start = performance.now();
        const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            const current = from + (to - from) * eased;
            fromRef.current = current;
            setDisplay(current);
            if (p < 1) raf = requestAnimationFrame(tick);
            else fromRef.current = to;
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);

    return display;
};

/* ═══════════════════════════════════════════════════════════════════════════
   3 · LIQUID BACKDROP
   ═══════════════════════════════════════════════════════════════════════════ */

const LiquidBackdrop = React.memo(function LiquidBackdrop() {
    const motes = useMemo(
        () =>
            Array.from({ length: 18 }, (_, i) => ({
                id: i,
                size: 3 + ((i * 7) % 9),
                x: (i * 37) % 100,
                y: (i * 53) % 100,
                duration: 26 + ((i * 5) % 18),
                delay: (i * 1.7) % 12,
                color: ACCENTS[i % ACCENTS.length],
            })),
        []
    );

    return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <Box
                component="svg"
                sx={{ position: 'absolute', width: 0, height: 0 }}
                aria-hidden
            >
                <defs>
                    <filter id="tf-liquid-company">
                        <feTurbulence type="fractalNoise" baseFrequency="0.008 0.012" numOctaves="2" seed="7" result="noise">
                            <animate attributeName="baseFrequency" dur="34s" values="0.008 0.012;0.014 0.006;0.008 0.012" repeatCount="indefinite" />
                        </feTurbulence>
                        <feDisplacementMap in="SourceGraphic" in2="noise" scale="70" xChannelSelector="R" yChannelSelector="G" />
                    </filter>
                </defs>
            </Box>

            {/* molten core */}
            <motion.div
                animate={{ x: [0, 70, -30, 0], y: [0, -45, 35, 0], scale: [1, 1.14, 0.9, 1] }}
                transition={{ duration: 30, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '-6%', right: '-4%',
                    width: 620, height: 620, borderRadius: '50%',
                    background: `radial-gradient(circle at 35% 35%, ${rgba(palette.rustGlow, 0.32)}, ${rgba(palette.rust, 0.14)} 45%, transparent 70%)`,
                    filter: 'url(#tf-liquid-company) blur(42px)',
                }}
            />
            {/* amber wash */}
            <motion.div
                animate={{ x: [0, -55, 40, 0], y: [0, 40, -35, 0], scale: [1, 0.88, 1.16, 1] }}
                transition={{ duration: 36, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', bottom: '-10%', left: '-6%',
                    width: 560, height: 560, borderRadius: '50%',
                    background: `radial-gradient(circle at 60% 40%, ${rgba(palette.amberGlow, 0.26)}, ${rgba(palette.amber, 0.12)} 48%, transparent 72%)`,
                    filter: 'url(#tf-liquid-company) blur(48px)',
                }}
            />
            {/* teal pool */}
            <motion.div
                animate={{ x: ['-50%', '-44%', '-56%', '-50%'], y: ['-50%', '-56%', '-45%', '-50%'], scale: [1, 1.08, 0.94, 1] }}
                transition={{ duration: 26, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '46%', left: '52%',
                    width: 760, height: 760, borderRadius: '50%',
                    background: `radial-gradient(circle, ${rgba(palette.tealGlow, 0.16)}, transparent 66%)`,
                    filter: 'blur(80px)',
                }}
            />

            {motes.map((m) => (
                <motion.span
                    key={m.id}
                    initial={{ opacity: 0 }}
                    animate={{
                        y: [`${m.y}%`, `${m.y - 45}%`, `${m.y}%`],
                        x: [`${m.x}%`, `${m.x + 14}%`, `${m.x}%`],
                        opacity: [0, 0.5, 0],
                        scale: [0.4, 1, 0.4],
                    }}
                    transition={{ duration: m.duration, delay: m.delay, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                        position: 'absolute', top: 0, left: 0,
                        width: m.size, height: m.size, borderRadius: '50%',
                        background: `radial-gradient(circle at 30% 30%, #fff, ${m.color})`,
                        boxShadow: `0 0 12px ${rgba(m.color, 0.5)}`,
                    }}
                />
            ))}

            <Box
                sx={{
                    position: 'absolute', inset: 0, opacity: 0.55,
                    backgroundImage: `linear-gradient(${rgba(palette.ink, 0.022)} 1px, transparent 1px),
                                      linear-gradient(90deg, ${rgba(palette.ink, 0.022)} 1px, transparent 1px)`,
                    backgroundSize: '72px 72px',
                    maskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 78%)',
                    WebkitMaskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 78%)',
                }}
            />
        </Box>
    );
});

/* ═══════════════════════════════════════════════════════════════════════════
   4 · PRIMITIVES
   ═══════════════════════════════════════════════════════════════════════════ */

const Rule = ({ color = palette.rust, w = 32 }) => (
    <Box
        sx={{
            width: w, height: 2, mt: 0.45, borderRadius: 3,
            background: color, opacity: .55,
        }}
    />
);

const GlassPanel = ({
    accent = palette.rust,
    delay = 0,
    lift = true,
    padding = { xs: 1.35, md: 1.55 },
    children,
    sx = {},
    ...rest
}) => (
    <motion.div
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.12 }}
        transition={{ duration: 0.3, delay }}
        style={{ height: '100%', width: '100%', minWidth: 0 }}
    >
        <Paper
            elevation={0}
            sx={{
                ...liquid(accent, { radius: 18, depth: 1 }),
                p: padding,
                height: '100%',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                ...(lift && {
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        borderColor: rgba(accent, .13),
                        boxShadow: `0 16px 34px ${rgba(accent, .12)}, 0 4px 12px rgba(25,57,78,.06)`,
                    },
                }),
                ...sx,
            }}
            {...rest}
        >
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                {children}
            </Box>
        </Paper>
    </motion.div>
);

const GlassIcon = ({ icon, color, size = 34 }) => (
    <Box
        sx={{
            width: size, height: size, flexShrink: 0,
            borderRadius: '10px',
            display: 'grid', placeItems: 'center',
            color: getContrastText(color),
            background: `linear-gradient(145deg, ${color}, ${rgba(color, .74)})`,
            border: '1px solid rgba(255,255,255,.55)',
            boxShadow: `0 10px 22px ${rgba(color, .27)}, inset 0 1px 0 rgba(255,255,255,.58)`,
            position: 'relative', overflow: 'hidden',
            '&::after': { content: '""', position: 'absolute', inset: 4, borderRadius: '7px', border: '1px solid rgba(255,255,255,.16)', pointerEvents: 'none' },
            '& svg': { fontSize: size * 0.48, position: 'relative', zIndex: 1 },
        }}
    >
        {icon}
    </Box>
);

const PanelHead = ({ icon, title, accent = palette.rust, chip, action, onAction, dense = false }) => (
    <Box
        sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 1, flexWrap: 'wrap', mb: dense ? .85 : 1,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: .8, minWidth: 0 }}>
            {icon && <GlassIcon icon={icon} color={accent} size={dense ? 34 : 38} />}
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: fontBody, fontWeight: 720,
                        fontSize: dense ? '0.76rem' : '0.84rem',
                        color: palette.ink, letterSpacing: '-0.015em', lineHeight: 1.2,
                    }}
                >
                    {title}
                </Typography>
                <Rule color={accent} w={dense ? 30 : 38} />
            </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {chip && (
                <Chip
                    label={chip}
                    size="small"
                    sx={{
                        height: 22, fontFamily: fontMono, fontSize: '0.58rem', fontWeight: 600,
                        letterSpacing: '0.04em', textTransform: 'uppercase',
                        color: getReadableColor(accent), bgcolor: rgba(accent, 0.09),
                        border: `1px solid ${rgba(accent, 0.16)}`,
                        backdropFilter: 'blur(6px)',
                    }}
                />
            )}
            {action && (
                <Button
                    size="small"
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                    onClick={onAction}
                    sx={{
                        color: getReadableColor(accent), fontFamily: fontBody, fontWeight: 600,
                        textTransform: 'none', fontSize: '0.72rem', borderRadius: '10px',
                        '&:hover': { bgcolor: rgba(accent, 0.07) },
                    }}
                >
                    {action}
                </Button>
            )}
        </Box>
    </Box>
);

const EmptyState = ({ icon, text, action, onAction }) => (
    <Box sx={{ textAlign: 'center', py: 2.5, px: 2, flex: 1, display: 'grid', placeContent: 'center' }}>
        <Box sx={{ display: 'grid', placeItems: 'center', mb: 1 }}>
            {React.cloneElement(icon, { sx: { fontSize: 28, color: palette.inkFaint } })}
        </Box>
        <Typography sx={{ color: palette.inkLight, fontSize: '0.82rem' }}>{text}</Typography>
        {action && (
            <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={onAction}
                sx={{
                    mt: 1.5, mx: 'auto', textTransform: 'none', fontWeight: 600,
                    borderRadius: '10px', px: 2, color: '#fff',
                    background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                    boxShadow: `0 8px 20px -8px ${rgba(palette.rust, 0.7)}`,
                    '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                }}
            >
                {action}
            </Button>
        )}
    </Box>
);

/* ── Modern Stat Card WITH OVERLAY ICON (compact) ────────────────────────── */

const ModernStatCard = ({ label, value, suffix = '', icon, gradient, delta, hint, delay = 0, loading }) => {
    const animated = useCountUp(value);
    const [accent, accent2 = accent] = gradient || [palette.blue, palette.sky];
    const accentInk = getReadableColor(accent);
    const iconContrast = getContrastText(accent);
    const trendColor = delta?.trend === 'down' ? palette.danger : palette.success;

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.32, delay }}
            style={{ height: '100%', minWidth: 0 }}
        >
            <Paper
                className="tf-color-card"
                elevation={0}
                sx={{
                    '--card-color': accent,
                    '--card-contrast': iconContrast,
                    height: '100%',
                    minHeight: 126,
                    p: 1.55,
                    display: 'flex',
                    gap: 1.2,
                    background: `radial-gradient(circle at 94% 12%, ${rgba(accent, .12)}, transparent 31%), linear-gradient(145deg, rgba(255,255,255,.995), ${rgba(accent, .065)}) !important`,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box className="tf-card-dotgrid" />
                <Box sx={{ position: 'absolute', right: -24, bottom: -44, width: 118, height: 118, borderRadius: '50%', border: `15px solid ${rgba(accent,.05)}`, pointerEvents: 'none' }} />
                <Box className="tf-card-microbars">
                    {[8, 14, 20, 12, 24].map((height, idx) => <Box component="span" key={idx} sx={{ height }} />)}
                </Box>

                <Box
                    className="tf-color-icon"
                    sx={{
                        '--card-color': accent,
                        '--card-contrast': iconContrast,
                        width: 46, height: 46, borderRadius: '13px', display: 'grid', placeItems: 'center',
                        flexShrink: 0, position: 'relative', zIndex: 2, '& svg': { fontSize: 21 },
                    }}
                >
                    {icon}
                </Box>

                <Box sx={{ minWidth: 0, flex: 1, position: 'relative', zIndex: 2 }}>
                    <Typography sx={{ fontSize: '0.61rem', fontWeight: 820, color: palette.inkSoft, letterSpacing: '.065em', textTransform: 'uppercase' }} noWrap>
                        {label}
                    </Typography>
                    {loading ? (
                        <Skeleton width="45%" height={33} sx={{ mt: .15, bgcolor: rgba(accent, .08) }} />
                    ) : (
                        <Typography sx={{ mt: .2, color: palette.ink, fontWeight: 860, fontSize: { xs: '1.58rem', md: '1.82rem' }, lineHeight: 1.02, letterSpacing: '-.045em', fontVariantNumeric: 'tabular-nums' }}>
                            {suffix === '%' ? `${Math.round(animated)}%` : compact(animated)}
                            {suffix && suffix !== '%' && <Box component="span" sx={{ fontSize: '.45em', ml: .3, color: palette.inkLight }}>{suffix}</Box>}
                        </Typography>
                    )}
                    <Box sx={{ mt: .7, display: 'flex', alignItems: 'center', gap: .55, minWidth: 0 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: `linear-gradient(135deg,${accent},${accent2})`, boxShadow: `0 0 0 4px ${rgba(accent,.09)}` }} />
                        <Typography sx={{ fontSize: '0.56rem', color: palette.inkLight, fontWeight: 560, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {hint || 'Live platform data'}
                        </Typography>
                    </Box>
                    {delta && <Typography sx={{ mt: .38, fontSize: '0.55rem', fontWeight: 760, color: trendColor }}>{delta.trend === 'down' ? '↓' : '↑'} {delta.label}</Typography>}
                </Box>

                <Box sx={{ position: 'absolute', right: 8, bottom: -16, opacity: .075, color: accentInk, transform: 'rotate(-8deg)', '& svg': { fontSize: 84 } }}>
                    {icon}
                </Box>
                <Box sx={{ position: 'absolute', inset: 'auto 0 0 0', height: 3, background: `linear-gradient(90deg, ${accent}, ${accent2}, transparent 92%)` }} />
            </Paper>
        </motion.div>
    );
};


/* ── Connected command-center card — screenshot structure ───────────────── */
const CommandCenterCard = ({ title, eyebrow, description, icon, accent, accent2, tags = [], meta, actionLabel = 'Open Dashboard', onClick }) => {
    const accentEnd = accent2 || accent;
    const accentInk = getReadableColor(accent);
    const iconContrast = getContrastText(accent);
    const actionContrast = getGradientContrastText(accent, accentEnd);

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: .34, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', minWidth: 0 }}
        >
            <Paper
                className="tf-color-card"
                elevation={0}
                onClick={onClick}
                sx={{
                    '--card-color': accent,
                    '--card-contrast': iconContrast,
                    height: '100%',
                    minHeight: 196,
                    p: 1.55,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    background: `radial-gradient(circle at 92% 10%, ${rgba(accent,.13)}, transparent 30%), radial-gradient(circle at 8% 110%, ${rgba(accentEnd,.075)}, transparent 34%), linear-gradient(145deg, rgba(255,255,255,.998), ${rgba(accent,.05)}) !important`,
                    border: `1px solid ${rgba(accent,.16)} !important`,
                    boxShadow: `0 13px 32px rgba(25,57,78,.085), 0 4px 11px ${rgba(accent,.055)} !important`,
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 22px 44px ${rgba(accent,.16)}, 0 6px 16px rgba(25,57,78,.075) !important`,
                        '& .command-icon': { transform: 'translateY(-2px) rotate(-4deg) scale(1.05)' },
                        '& .command-arrow': { transform: 'translateX(3px)' },
                    },
                }}
            >
                <Box className="tf-card-dotgrid" sx={{ right: 10, top: 12, opacity: .08 }} />
                <Box sx={{ position: 'absolute', width: 166, height: 166, right: -76, top: -69, borderRadius: '50%', bgcolor: rgba(accent,.075), pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', width: 150, height: 82, right: 8, bottom: -43, borderRadius: '46% 54% 0 0', bgcolor: rgba(accentEnd,.065), transform: 'rotate(-11deg)', pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', right: 8, bottom: -2, opacity: .055, color: accentInk, transform: 'rotate(-10deg)', pointerEvents: 'none', '& svg': { fontSize: 94 } }}>{icon}</Box>
                <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: `linear-gradient(180deg, ${accent}, ${accentEnd}, transparent)` }} />

                <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Box
                        className="command-icon tf-color-icon"
                        sx={{ '--card-color': accent, '--card-contrast': iconContrast, width: 44, height: 44, borderRadius: '13px', display: 'grid', placeItems: 'center', flexShrink: 0, transition: 'transform .25s ease', '& svg': { fontSize: 21 } }}
                    >
                        {icon}
                    </Box>
                    <Box sx={{ height: 22, px: .8, borderRadius: '999px', display: 'inline-flex', alignItems: 'center', gap: .4, color: accentInk, bgcolor: rgba(accent,.075), border: `1px solid ${rgba(accent,.12)}`, fontSize: '.47rem', fontWeight: 830, letterSpacing: '.065em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: accent, boxShadow: `0 0 0 3px ${rgba(accent,.09)}` }} />
                        {eyebrow}
                    </Box>
                </Box>

                <Typography sx={{ position: 'relative', zIndex: 2, mt: 1.2, color: palette.ink, fontSize: '.94rem', fontWeight: 860, lineHeight: 1.18, letterSpacing: '-.027em' }}>
                    {title}
                </Typography>
                <Typography sx={{ position: 'relative', zIndex: 2, mt: .52, color: palette.inkLight, fontSize: '.62rem', fontWeight: 520, lineHeight: 1.58, minHeight: 40, maxWidth: '94%' }}>
                    {description}
                </Typography>

                <Box sx={{ position: 'relative', zIndex: 2, mt: .9, display: 'flex', flexWrap: 'wrap', gap: .45 }}>
                    {tags.slice(0, 3).map((tag) => (
                        <Box key={tag} sx={{ height: 21, px: .7, borderRadius: '999px', display: 'inline-flex', alignItems: 'center', color: accentInk, bgcolor: rgba(accent,.065), border: `1px solid ${rgba(accent,.10)}`, fontSize: '.47rem', fontWeight: 730 }}>
                            {tag}
                        </Box>
                    ))}
                </Box>

                <Box sx={{ position: 'relative', zIndex: 2, mt: 'auto', pt: 1.05, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ color: palette.inkFaint, fontSize: '.51rem', fontWeight: 650 }} noWrap>{meta}</Typography>
                    <Button
                        size="small"
                        onClick={(event) => { event.stopPropagation(); onClick?.(); }}
                        endIcon={<ArrowForwardIcon className="command-arrow" sx={{ fontSize: '12px !important', transition: 'transform .2s ease' }} />}
                        sx={{
                            minHeight: '28px !important', px: '10px !important', borderRadius: '9px !important',
                            color: `${actionContrast} !important`, fontSize: '.51rem !important',
                            background: `linear-gradient(135deg, ${accent}, ${accentEnd}) !important`,
                            boxShadow: `0 7px 16px ${rgba(accent,.22)} !important`,
                        }}
                    >
                        {actionLabel}
                    </Button>
                </Box>
            </Paper>
        </motion.div>
    );
};


/* ── quick action tile ────────────────────────────────────────────────────── */

const QuickAction = ({ icon, label, desc, accent, onClick, badge, delay = 0 }) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay, ease: [0.22, 1, 0.36, 1] }}
            whileTap={{ scale: 0.98 }}
            style={{ minWidth: 0 }}
        >
            <Box
                ref={ref}
                role="button"
                tabIndex={0}
                onClick={onClick}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                sx={{
                    ...liquid(accent, { radius: 15, depth: 0.8 }),
                    cursor: 'pointer',
                    p: 1.1,
                    display: 'flex', alignItems: 'center', gap: 1,
                    minHeight: 60, minWidth: 0,
                    '&::after': sheenLayer(accent),
                    '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 14px 28px -12px ${rgba(accent, 0.4)}`,
                    },
                    '&:hover .tf-arrow': { transform: 'translateX(4px)', opacity: 1 },
                    '&:focus-visible': { outline: `2px solid ${rgba(accent, 0.6)}`, outlineOffset: 2 },
                }}
            >
                <Box
                    sx={{
                        width: 30,
                        height: 30,
                        borderRadius: '9px',
                        display: 'grid',
                        placeItems: 'center',
                        color: '#fff',
                        background: `linear-gradient(145deg, ${accent}, ${rgba(accent, 0.72)})`,
                        border: '1px solid rgba(255,255,255,.48)',
                        boxShadow: `0 7px 16px ${rgba(accent,.22)}, inset 0 1px 0 rgba(255,255,255,.5)`,
                        flexShrink: 0,
                        '& svg': { fontSize: 15 },
                    }}
                >
                    {icon}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1, position: 'relative', zIndex: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.76rem', color: palette.ink, lineHeight: 1.2 }} noWrap>
                        {label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: palette.inkLight }} noWrap>
                        {desc}
                    </Typography>
                </Box>
                {badge > 0 && (
                    <Box
                        sx={{
                            px: 0.7, height: 16, borderRadius: '8px', display: 'grid', placeItems: 'center',
                            fontFamily: fontMono, fontSize: '0.52rem', fontWeight: 700, color: '#fff',
                            background: `linear-gradient(140deg, ${palette.danger}, #96201A)`,
                            boxShadow: `0 4px 12px -3px ${rgba(palette.danger, 0.7)}`,
                            position: 'relative', zIndex: 1,
                        }}
                    >
                        {badge > 99 ? '99+' : badge}
                    </Box>
                )}
                <ArrowForwardIcon
                    className="tf-arrow"
                    sx={{
                        fontSize: 14, color: accent, opacity: 0.3, position: 'relative', zIndex: 1,
                        transition: 'transform .3s cubic-bezier(.22,1,.36,1), opacity .3s ease',
                    }}
                />
            </Box>
        </motion.div>
    );
};

/* ── list rows ────────────────────────────────────────────────────────────── */

const STATUS_TONE = {
    completed: palette.success,
    confirmed: palette.pine,
    active: palette.pine,
    ongoing: palette.teal,
    pending: palette.warning,
    cancelled: palette.danger,
    rejected: palette.danger,
};

const toneFor = (status) => STATUS_TONE[String(status || '').toLowerCase()] || palette.inkLight;

const LiquidRow = ({ accent, onClick, children, index = 0 }) => (
    <motion.div
        initial={{ opacity: 0, x: -12 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: Math.min(index, 8) * 0.04, ease: [0.22, 1, 0.36, 1] }}
    >
        <Box
            onClick={onClick}
            sx={{
                p: 0.9, mb: 0.6, borderRadius: '12px',
                display: 'flex', alignItems: 'center', gap: 1, minWidth: 0,
                cursor: onClick ? 'pointer' : 'default',
                background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.7)}, ${rgba('#FFFFFF', 0.34)})`,
                border: `1px solid ${rgba('#FFFFFF', 0.7)}`,
                boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.8)}`,
                transition: 'transform .3s cubic-bezier(.22,1,.36,1), box-shadow .3s ease, background .3s ease',
                '&:hover': {
                    transform: 'translateX(4px)',
                    background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.9)}, ${rgba(accent, 0.08)})`,
                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 10px 22px -12px ${rgba(accent, 0.5)}`,
                },
            }}
        >
            {children}
        </Box>
    </motion.div>
);

const StatusPill = ({ status }) => {
    const tone = toneFor(status);
    return (
        <Box
            sx={{
                px: 0.8, py: 0.25, borderRadius: '7px', flexShrink: 0,
                fontFamily: fontMono, fontSize: '0.52rem', fontWeight: 600,
                letterSpacing: '0.05em', textTransform: 'uppercase',
                color: tone, bgcolor: rgba(tone, 0.1), border: `1px solid ${rgba(tone, 0.18)}`,
            }}
        >
            {status || 'unknown'}
        </Box>
    );
};

/* ── Glass Pill ───────────────────────────────────────────────────────────── */

const GlassPill = ({ label, color = palette.rust, icon, size = 'sm', filled = false }) => (
    <Box
        sx={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            borderRadius: '999px', fontWeight: 600, whiteSpace: 'nowrap',
            backdropFilter: 'blur(8px) saturate(170%)',
            WebkitBackdropFilter: 'blur(8px) saturate(170%)',
            height: size === 'sm' ? 20 : 24,
            px: size === 'sm' ? 0.8 : 1.2,
            fontSize: size === 'sm' ? '0.55rem' : '0.62rem',
            letterSpacing: '0.03em',
            fontFamily: fontMono,
            ...(filled
                ? {
                      color: '#fff',
                      background: `linear-gradient(150deg, ${rgba(color, 0.92)}, ${rgba(color, 0.7)})`,
                      border: `1px solid ${rgba('#FFFFFF', 0.45)}`,
                      boxShadow: `0 4px 10px ${rgba(color, 0.25)}, inset 0 1px 0 ${rgba('#FFFFFF', 0.6)}`,
                  }
                : {
                      color,
                      background: rgba(color, 0.08),
                      border: `1px solid ${rgba(color, 0.2)}`,
                      boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.6)}`,
                  }),
        }}
    >
        {icon}
        {label}
    </Box>
);

/* ── Glass Solid Button (now supports a `disabled` state) ───────────────── */

const GlassSolidButton = ({ label, color, icon, onClick, variant = 'solid', disabled = false }) => (
    <Button
        size="small"
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        startIcon={icon}
        sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.68rem',
            py: 0.6,
            px: 1.75,
            minHeight: 30,
            transition: 'all 0.28s cubic-bezier(0.34,1.56,0.64,1)',
            opacity: disabled ? 0.55 : 1,
            ...(variant === 'solid'
                ? {
                      color: '#fff',
                      background: `linear-gradient(145deg, ${rgba(color, 0.96)}, ${color})`,
                      boxShadow: `0 8px 20px ${rgba(color, 0.3)}, 0 3px 8px ${rgba(color, 0.18)}, inset 0 1px 0 ${rgba('#FFFFFF', 0.6)}`,
                  }
                : {
                      color,
                      background: rgba(color, 0.1),
                      backdropFilter: 'blur(10px) saturate(175%)',
                      WebkitBackdropFilter: 'blur(10px) saturate(175%)',
                      border: `1px solid ${rgba(color, 0.25)}`,
                      boxShadow: `0 6px 16px ${rgba(color, 0.12)}, inset 0 1px 0 ${rgba('#FFFFFF', 0.7)}`,
                  }),
            '&.Mui-disabled': {
                color: variant === 'solid' ? '#fff' : color,
                background: variant === 'solid'
                    ? `linear-gradient(145deg, ${rgba(color, 0.5)}, ${rgba(color, 0.4)})`
                    : rgba(color, 0.06),
                boxShadow: 'none',
            },
            '&:hover': disabled ? {} : { transform: 'translateY(-2px)' },
            '&:active': { transform: 'translateY(0)' },
        }}
    >
        {label}
    </Button>
);

/* ── Detail Tile ──────────────────────────────────────────────────────────── */

const DetailTile = ({ icon, color, label, value }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, ...glassWell(14) }}>
        <Box
            sx={{
                width: 32,
                height: 32,
                borderRadius: '10px',
                display: 'grid',
                placeItems: 'center',
                color: '#fff',
                background: `linear-gradient(150deg, ${rgba(color, 0.9)}, ${rgba(color, 0.6)})`,
                boxShadow: `0 4px 12px ${rgba(color, 0.3)}, inset 0 1px 0 ${rgba('#FFFFFF', 0.5)}`,
                flexShrink: 0,
                '& svg': { fontSize: 16 },
            }}
        >
            {React.cloneElement(icon, { sx: { fontSize: 16 } })}
        </Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: palette.inkLight, fontWeight: 600, letterSpacing: '0.04em', fontFamily: fontMono, fontSize: '0.52rem' }}>
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ color: palette.ink, wordBreak: 'break-word', fontSize: '0.78rem' }}>
                {value}
            </Typography>
        </Box>
    </Box>
);

/* ── Intro Status Pill (shared by card + dialog) ─────────────────────────── */

const IntroStatusPill = ({ status }) => {
    if (status === 'accepted') {
        return <GlassPill label="Connected" color={palette.pine} icon={<VerifiedIcon sx={{ fontSize: 11 }} />} filled />;
    }
    if (status === 'pending') {
        return <GlassPill label="Pending" color={palette.amber} icon={<HourglassTopIcon sx={{ fontSize: 11 }} />} filled />;
    }
    return null;
};

/* ── Modern Company Card with Profile Image ──────────────────────────────── */

const ModernCompanyCard = ({ company, accentColor, gradient, onClick, introStatus, onSendIntro }) => {
    const [gradientStart, gradientEnd] = gradient || [accentColor || palette.rust, palette.rustGlow];
    const isPending = introStatus === 'pending';
    const isConnected = introStatus === 'accepted';
    const coverImage = getCompanyCoverUrl(company);
    const logoImage = getCompanyLogoUrl(company);
    const companyName = company.companyName || company.name || 'Company';
    const initials = companyName.split(/\s+/).slice(0, 2).map((part) => part?.[0] || '').join('').toUpperCase() || 'C';
    const primaryService = company.primaryService || company.primary_service || (Array.isArray(company.services) ? company.services[0] : '') || company.companyType || 'Telecom business';
    const region = company.primaryRegion || company.primary_region || (Array.isArray(company.regions) ? company.regions[0] : '') || company.country || 'Global';

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -5 }}
            style={{ height: '100%' }}
        >
            <Paper
                elevation={0}
                className="tf-dashboard-company-card tf-company-image-card"
                onClick={onClick}
                sx={{
                    '--company-accent': gradientStart,
                    minHeight: 210,
                    height: '100%',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '20px',
                    background: coverImage
                        ? `linear-gradient(180deg, rgba(10,17,24,.06) 0%, rgba(10,17,24,.18) 36%, rgba(7,12,18,.90) 100%), url("${coverImage}") center/cover no-repeat`
                        : `linear-gradient(145deg, ${gradientStart}, ${gradientEnd})`,
                    border: '1px solid rgba(255,255,255,.52)',
                    boxShadow: '0 14px 34px rgba(25,57,78,.13), inset 0 1px 0 rgba(255,255,255,.38)',
                    isolation: 'isolate',
                    filter: isPending ? 'saturate(.82)' : 'none',
                    opacity: isPending ? .86 : 1,
                    transition: 'transform .25s ease, box-shadow .25s ease, border-color .25s ease',
                    '&:hover': {
                        borderColor: 'rgba(255,255,255,.76)',
                        boxShadow: `0 22px 48px ${rgba(gradientStart,.20)}, 0 8px 18px rgba(25,57,78,.10)`,
                        '& .tf-company-image-zoom': { transform: 'scale(1.045)' },
                        '& .tf-company-shine': { transform: 'translateX(155%) skewX(-18deg)' },
                    },
                }}
            >
                {coverImage && <Box className="tf-company-image-zoom" aria-hidden="true" sx={{ position:'absolute', inset:0, zIndex:-3, background:`url("${coverImage}") center/cover no-repeat`, transition:'transform .55s cubic-bezier(.22,1,.36,1)' }} />}
                <Box aria-hidden="true" sx={{ position:'absolute', inset:0, zIndex:-2, background:'linear-gradient(180deg,rgba(5,10,15,.04) 0%,rgba(5,10,15,.20) 44%,rgba(5,10,15,.92) 100%)' }} />
                <Box aria-hidden="true" sx={{ position:'absolute', inset:'auto -12% -46% 26%', height:'72%', borderRadius:'50%', background:`radial-gradient(circle, ${rgba(gradientStart,.34)}, transparent 66%)`, filter:'blur(20px)', zIndex:-1 }} />
                <Box className="tf-company-shine" aria-hidden="true" sx={{ position:'absolute', top:-24, bottom:-24, left:'-38%', width:'26%', background:'linear-gradient(90deg,transparent,rgba(255,255,255,.28),transparent)', transform:'translateX(-15%) skewX(-18deg)', transition:'transform .72s ease', pointerEvents:'none' }} />

                <Box sx={{ position:'absolute', top:11, right:11, zIndex:3, display:'flex', gap:.6 }}>
                    {company.isVerified && <Box sx={{ width:24,height:24,borderRadius:'50%',display:'grid',placeItems:'center',bgcolor:'rgba(13,23,18,.68)',backdropFilter:'blur(8px)',border:'1px solid rgba(255,255,255,.32)' }}><VerifiedIcon sx={{ fontSize:14, color:'#fff' }} /></Box>}
                    {(isPending || isConnected) && <Box sx={{ px:.9,height:24,borderRadius:'999px',display:'inline-flex',alignItems:'center',gap:.45,color:'#fff',bgcolor:isConnected?'rgba(20,132,90,.86)':'rgba(13,23,18,.72)',backdropFilter:'blur(8px)',border:'1px solid rgba(255,255,255,.22)',fontSize:'.52rem',fontWeight:800 }}>{isConnected ? <CheckCircleIcon sx={{fontSize:11}}/> : <HourglassTopIcon sx={{fontSize:11}}/>}{isConnected?'Connected':'Pending'}</Box>}
                </Box>

                <Box sx={{ position:'absolute', left:0, right:0, bottom:0, p:1.35, zIndex:2 }}>
                    <Box sx={{ display:'flex', alignItems:'flex-end', gap:1.05, minWidth:0 }}>
                        <Avatar
                            src={logoImage || undefined}
                            variant="rounded"
                            sx={{ width:52,height:52,borderRadius:'14px',bgcolor:'#fff',color:gradientStart,fontWeight:900,border:'2px solid rgba(255,255,255,.82)',boxShadow:'0 9px 22px rgba(0,0,0,.24)',flexShrink:0,'& img':{objectFit:'contain',padding:'4px'} }}
                        >{!logoImage && initials}</Avatar>
                        <Box sx={{ minWidth:0, flex:1, pb:.15 }}>
                            <Typography sx={{ color:'#fff',fontWeight:850,fontSize:'.88rem',lineHeight:1.16,textShadow:'0 2px 8px rgba(0,0,0,.38)' }} noWrap>{companyName}</Typography>
                            <Typography sx={{ mt:.3,color:'rgba(255,255,255,.76)',fontSize:'.6rem',fontWeight:600 }} noWrap>{primaryService}</Typography>
                        </Box>
                    </Box>

                    <Box sx={{ mt:1, display:'flex',alignItems:'center',justifyContent:'space-between',gap:.7,borderTop:'1px solid rgba(255,255,255,.18)',pt:.8 }}>
                        <Box sx={{ minWidth:0,display:'flex',alignItems:'center',gap:.45,color:'rgba(255,255,255,.78)' }}><LocationIcon sx={{fontSize:12}}/><Typography sx={{fontSize:'.54rem',fontWeight:650}} noWrap>{region}</Typography></Box>
                        <Box sx={{ display:'flex',alignItems:'center',gap:.5,flexShrink:0 }}>
                            <Box sx={{ px:.7,height:21,borderRadius:'999px',display:'inline-flex',alignItems:'center',color:'#fff',bgcolor:'rgba(255,255,255,.13)',border:'1px solid rgba(255,255,255,.18)',backdropFilter:'blur(8px)',fontSize:'.48rem',fontWeight:750 }}>{(company.companyType || 'Company').replace(/_/g,' ')}</Box>
                            {!isConnected && !isPending && <Tooltip title="Send AI introduction"><IconButton onClick={(e)=>{e.stopPropagation();onSendIntro?.(company);}} sx={{width:25,height:25,color:'#fff',bgcolor:'rgba(255,255,255,.14)',border:'1px solid rgba(255,255,255,.18)','&:hover':{bgcolor:'rgba(255,255,255,.24)'}}}><PeopleIcon sx={{fontSize:13}}/></IconButton></Tooltip>}
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </motion.div>
    );
};

/* ── Ads Banner ───────────────────────────────────────────────────────────── */

const AdsBanner = ({ ad }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            whileHover={{ scale: 1.02, y: -6 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            style={{ width: '100%', height: '100%', minHeight: 160 }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Paper
                sx={{
                    ...liquid(palette.amber, { radius: 18 }),
                    cursor: ad.linkUrl ? 'pointer' : 'default',
                    height: '100%',
                    minHeight: 160,
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                    },
                }}
                onClick={() => {
                    if (ad.linkUrl) {
                        openSafeExternalUrl(ad.linkUrl);
                        const adRef = doc(db, 'ads', ad.id);
                        updateDoc(adRef, {
                            clicks: increment(1),
                            updatedAt: serverTimestamp(),
                        }).catch((err) => console.warn('Failed to track click:', err));
                    }
                }}
            >
                {ad.type === 'video' && ad.mediaUrl ? (
                    <video src={ad.mediaUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted autoPlay loop playsInline />
                ) : ad.mediaUrl ? (
                    <img
                        src={ad.mediaUrl}
                        alt={ad.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            minHeight: 160,
                            gap: 1,
                            background: `linear-gradient(150deg, ${palette.canvasDeep}, ${palette.canvas})`,
                        }}
                    >
                        <GlassIcon icon={<StorefrontIcon />} color={palette.amber} size={40} />
                        <Typography sx={{ color: palette.inkLight, fontWeight: 600, fontSize: '0.85rem' }}>
                            {ad.title || 'Sponsored Content'}
                        </Typography>
                    </Box>
                )}

                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '75%',
                        background: 'linear-gradient(transparent 0%, rgba(20,12,6,0.42) 40%, rgba(20,12,6,0.86) 100%)',
                        transition: 'opacity 0.4s ease',
                        opacity: isHovered ? 0.95 : 0.72,
                    }}
                />

                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 1.75, zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.2 }}>
                        <Typography
                            sx={{
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                textShadow: '0 2px 10px rgba(0,0,0,0.5)',
                                fontFamily: fontDisplay,
                            }}
                        >
                            {ad.title || 'Sponsored'}
                        </Typography>
                        {ad.isActive && (
                            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 12, height: 12 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: palette.success }} />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        width: 14,
                                        height: 14,
                                        borderRadius: '50%',
                                        border: `1.5px solid ${palette.success}`,
                                        animation: 'tfPulse 2.2s ease-out infinite',
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.92)',
                            textShadow: '0 1px 5px rgba(0,0,0,0.4)',
                            fontSize: { xs: '0.66rem', sm: '0.74rem' },
                            fontWeight: 500,
                            mb: 0.75,
                        }}
                    >
                        {ad.description || ad.companyName || 'Sponsored Content'}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                        {[
                            { icon: <VisibilityIcon sx={{ fontSize: 10 }} />, label: `${ad.impressions || 0} views` },
                            { icon: <TrendingUpIcon sx={{ fontSize: 10 }} />, label: `${ad.clicks || 0} clicks` },
                            ...(ad.ctr ? [{ icon: null, label: `CTR ${ad.ctr}%` }] : []),
                        ].map((m, i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.4,
                                    px: 0.8,
                                    height: 18,
                                    borderRadius: '999px',
                                    fontSize: '0.5rem',
                                    fontWeight: 600,
                                    color: '#fff',
                                    background: 'rgba(255,255,255,0.14)',
                                    backdropFilter: 'blur(6px)',
                                    boxShadow: 'inset 1px 1px 2px rgba(255,255,255,0.3), inset -1px -2px 4px rgba(0,0,0,0.2)',
                                }}
                            >
                                {m.icon}
                                {m.label}
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
                    <GlassPill label="Sponsored" color={palette.amber} filled size="sm" />
                </Box>
                <Box sx={{ position: 'absolute', top: 10, left: 10 }}>
                    <Box
                        sx={{
                            px: 0.8,
                            height: 18,
                            borderRadius: '999px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            fontSize: '0.5rem',
                            fontWeight: 700,
                            textTransform: 'capitalize',
                            color: palette.ink,
                            background: 'rgba(255,253,250,0.92)',
                            boxShadow: 'inset 1px 1px 2px rgba(255,255,255,0.9), inset -1px -2px 4px rgba(0,0,0,0.10)',
                        }}
                    >
                        {ad.type || 'banner'}
                    </Box>
                </Box>

                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        borderRadius: '50%',
                        width: 44,
                        height: 44,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        background: 'rgba(255,255,255,0.14)',
                        backdropFilter: 'blur(12px)',
                        boxShadow: 'inset 2px 2px 5px rgba(255,255,255,0.35), inset -3px -4px 9px rgba(0,0,0,0.25)',
                        opacity: isHovered ? 1 : 0,
                        transition: 'all 0.45s cubic-bezier(0.34,1.56,0.64,1)',
                        transform: isHovered ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0.5)',
                        pointerEvents: 'none',
                    }}
                >
                    <ArrowForwardIcon sx={{ fontSize: 20 }} />
                </Box>
            </Paper>
        </motion.div>
    );
};

/* ── Notification Card ───────────────────────────────────────────────────── */

const NotificationCard = ({ notification, onAccept, onDecline, onView, onChat }) => {
    const getIcon = () => {
        switch (notification.type) {
            case 'meeting_request': return <EventIcon sx={{ fontSize: 15 }} />;
            case 'invitation': return <PeopleIcon sx={{ fontSize: 15 }} />;
            case 'ai_message': return <AutoAwesomeIcon sx={{ fontSize: 15 }} />;
            case 'ai_sent': return <SendIcon sx={{ fontSize: 15 }} />;
            case 'ai_accepted': return <CheckCircleIcon sx={{ fontSize: 15 }} />;
            case 'invitation_sent': return <SendIcon sx={{ fontSize: 15 }} />;
            case 'invitation_accepted': return <CheckCircleIcon sx={{ fontSize: 15 }} />;
            case 'meeting_reminder': return <ScheduleIcon sx={{ fontSize: 15 }} />;
            default: return <NotificationIcon sx={{ fontSize: 15 }} />;
        }
    };

    const getColor = () => {
        switch (notification.type) {
            case 'meeting_request': return palette.rust;
            case 'invitation': return palette.amber;
            case 'ai_message': return palette.plum;
            case 'ai_sent': return palette.teal;
            case 'ai_accepted': return palette.pine;
            case 'invitation_sent': return palette.teal;
            case 'invitation_accepted': return palette.pine;
            case 'meeting_reminder': return palette.teal;
            default: return palette.inkLight;
        }
    };

    const color = getColor();

    const getActionButtons = () => {
        if (notification.type === 'invitation' || notification.type === 'meeting_request') {
            return (
                <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                    <GlassSolidButton
                        label={notification.type === 'meeting_request' ? 'Accept' : 'Accept'}
                        color={palette.pine}
                        icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
                        onClick={() => onAccept(notification)}
                    />
                    <GlassSolidButton
                        label="Decline"
                        color={palette.danger}
                        variant="soft"
                        icon={<CancelIcon sx={{ fontSize: 12 }} />}
                        onClick={() => onDecline(notification)}
                    />
                </Box>
            );
        }

        if (notification.type === 'ai_accepted' || notification.type === 'invitation_accepted') {
            return (
                <Box sx={{ display: 'flex', gap: 0.75, mt: 1 }}>
                    <GlassSolidButton
                        label="Chat"
                        color={palette.amber}
                        variant="soft"
                        icon={<ChatIcon sx={{ fontSize: 12 }} />}
                        onClick={() => onChat(notification)}
                    />
                </Box>
            );
        }

        if (notification.type === 'ai_sent' || notification.type === 'invitation_sent') {
            return (
                <Box sx={{ mt: 1 }}>
                    <GlassPill label="Awaiting Response" color={palette.amber} />
                </Box>
            );
        }

        return null;
    };

    return (
        <motion.div
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
            <Paper
                elevation={0}
                sx={{
                    p: 1.2,
                    mb: 0.75,
                    ...liquid(color, { radius: 14, depth: 0.55 }),
                    transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
                    '&:hover': { transform: 'translateY(-2px)' },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                    <Box
                        sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '9px',
                            display: 'grid',
                            placeItems: 'center',
                            color: '#fff',
                            background: `linear-gradient(150deg, ${rgba(color, 0.9)}, ${rgba(color, 0.6)})`,
                            boxShadow: `0 4px 12px ${rgba(color, 0.3)}`,
                            flexShrink: 0,
                        }}
                    >
                        {getIcon()}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontWeight: 600, color: palette.ink, fontSize: '0.76rem' }}>
                                {notification.title}
                            </Typography>
                            {!notification.read && <GlassPill label="New" color={palette.rust} size="sm" filled />}
                            <GlassPill label={formatRelative(notification.time)} color={palette.inkLight} size="sm" />
                        </Box>
                        <Typography sx={{ color: palette.inkSoft, fontSize: '0.7rem', mt: 0.3, lineHeight: 1.45 }}>
                            {notification.message}
                        </Typography>
                        {getActionButtons()}
                    </Box>
                    <IconButton
                        size="small"
                        onClick={() => onView(notification)}
                        sx={{
                            color: palette.inkLight,
                            width: 24,
                            height: 24,
                            borderRadius: '7px',
                            transition: 'all 0.25s ease',
                            '&:hover': { ...glassWell(7), color },
                        }}
                    >
                        <MoreVertIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Box>
            </Paper>
        </motion.div>
    );
};

/* ── Floating Support Button ─────────────────────────────────────────────── */

const FloatingSupportButton = ({ onNavigate }) => {
    const [unreadSupportCount, setUnreadSupportCount] = useState(0);
    const [showNotification, setShowNotification] = useState(false);
    const { currentUser, userData, companyData } = useAuth();

    const companyId =
        companyData?.companyId || userData?.companyId || companyData?.id || userData?.id || currentUser?.uid;

    useEffect(() => {
        if (!companyId) return undefined;

        const ticketsRef = collection(db, 'supportTickets');
        const q = query(ticketsRef, where('companyId', '==', companyId), where('status', 'in', ['open', 'in_progress']));

        const unsubscribe = onSnapshot(
            q,
            async (snapshot) => {
                try {
                    let unread = 0;
                    for (const ticketDoc of snapshot.docs) {
                        const messagesRef = collection(db, 'supportTickets', ticketDoc.id, 'messages');
                        const msgQuery = query(messagesRef, where('read', '==', false), where('senderType', '==', 'admin'));
                        const msgSnapshot = await getCountFromServer(msgQuery);
                        unread += msgSnapshot.data().count;
                    }
                    setUnreadSupportCount(unread);
                    setShowNotification(unread > 0);
                } catch (error) {
                    console.error('Error checking support unread:', error);
                }
            },
            (error) => console.error('Error checking support unread:', error)
        );

        return () => unsubscribe();
    }, [companyId]);

    return (
        <>
            <Box sx={{ position: 'fixed', bottom: { xs: 16, md: 24 }, right: { xs: 16, md: 24 }, zIndex: 1000 }}>
                <Badge
                    badgeContent={unreadSupportCount}
                    sx={{
                        '& .MuiBadge-badge': {
                            fontFamily: fontMono,
                            fontSize: '0.58rem',
                            fontWeight: 700,
                            height: 20,
                            minWidth: 20,
                            borderRadius: '10px',
                            color: '#fff',
                            background: `linear-gradient(140deg, ${palette.danger}, #94211B)`,
                            border: `2px solid ${rgba('#FFFFFF', 0.9)}`,
                            boxShadow: `0 6px 16px -4px ${rgba(palette.danger, 0.8)}`,
                            animation: unreadSupportCount > 0 ? 'tfPulse 2s ease-in-out infinite' : 'none',
                        },
                    }}
                >
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.4, type: 'spring', stiffness: 240, damping: 20 }}
                        whileHover={{ scale: 1.05, y: -3 }}
                        whileTap={{ scale: 0.96 }}
                    >
                        <Fab
                            variant="extended"
                            onClick={() => onNavigate('/dashboard/support')}
                            sx={{
                                px: 2,
                                height: 44,
                                borderRadius: '16px',
                                textTransform: 'none',
                                color: '#fff',
                                fontFamily: fontBody,
                                fontWeight: 600,
                                fontSize: '0.78rem',
                                background: `linear-gradient(140deg, ${palette.plum}, #3B0E4C)`,
                                border: `1px solid ${rgba('#FFFFFF', 0.28)}`,
                                boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.4)}, 0 14px 34px -12px ${rgba(palette.plum, 0.9)}`,
                                '&:hover': {
                                    background: `linear-gradient(140deg, ${palette.plumGlow}, ${palette.plum})`,
                                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.5)}, 0 18px 44px -12px ${rgba(palette.plum, 1)}`,
                                },
                            }}
                        >
                            <SupportAgentIcon sx={{ mr: 1, fontSize: 18 }} />
                            Support
                            {unreadSupportCount > 0 && (
                                <Box
                                    sx={{
                                        ml: 1,
                                        px: 0.8,
                                        height: 18,
                                        minWidth: 18,
                                        borderRadius: '999px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.58rem',
                                        fontWeight: 700,
                                        bgcolor: '#fff',
                                        color: palette.plum,
                                    }}
                                >
                                    {unreadSupportCount}
                                </Box>
                            )}
                        </Fab>
                    </motion.div>
                </Badge>
            </Box>

            <AnimatePresence>
                {showNotification && unreadSupportCount > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.9 }}
                        transition={{ duration: 0.3 }}
                        style={{ position: 'fixed', bottom: 88, right: 24, zIndex: 999, maxWidth: 300, width: '100%' }}
                        onClick={() => {
                            setShowNotification(false);
                            onNavigate('/dashboard/support');
                        }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                p: 1.75,
                                ...liquid(palette.admin, { radius: 18 }),
                                cursor: 'pointer',
                                transition: 'all 0.25s ease',
                                '&:hover': { transform: 'translateY(-3px)' },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                                <Box
                                    sx={{
                                        width: 34,
                                        height: 34,
                                        borderRadius: '10px',
                                        display: 'grid',
                                        placeItems: 'center',
                                        color: '#fff',
                                        background: `linear-gradient(150deg, ${palette.admin}, ${palette.purple})`,
                                        boxShadow: `0 4px 12px ${rgba(palette.admin, 0.3)}`,
                                    }}
                                >
                                    <SupportAgentIcon sx={{ fontSize: 17 }} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 600, color: palette.ink, fontSize: '0.78rem' }}>
                                        {unreadSupportCount} new message{unreadSupportCount > 1 ? 's' : ''}
                                    </Typography>
                                    <Typography sx={{ color: palette.inkLight, fontSize: '0.68rem' }}>
                                        Click to view and reply
                                    </Typography>
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowNotification(false);
                                    }}
                                    sx={{ color: palette.inkLight, p: 0.5, borderRadius: '8px', '&:hover': glassWell(8) }}
                                >
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Box>
                        </Paper>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

/* ── Modern User Card (All Users tab) ─────────────────────────────────── */

const ModernUserCard = ({ user, company, gradient, onClick, introStatus, onSendIntro }) => {
    const [gradientStart, gradientEnd] = gradient || [palette.teal, palette.tealGlow];
    const isPending = introStatus === 'pending';
    const isConnected = introStatus === 'accepted';
    const displayName = user.displayName || user.firstName || user.name || 'User';
    const initials = displayName.charAt(0).toUpperCase();
    const role = user.role || user.designation || user.businessTitle || 'Member';
    const photoURL = getPersonPhotoUrl(user);
    const companyCover = getCompanyCoverUrl(company || {}) || (!company?.id ? (user.companyCoverImage || user.companyCoverUrl || '') : '');
    const companyLogo = getCompanyLogoUrl(company || {}) || (!company?.id ? (user.companyLogo || user.companyLogoUrl || '') : '');

    return (
        <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -6 }}
            style={{ height: '100%' }}
        >
            <Paper
                elevation={0}
                onClick={onClick}
                sx={{
                    ...liquid(gradientStart, { radius: 20, depth: 0.7 }),
                    height: '100%',
                    minHeight: 172,
                    cursor: onClick ? 'pointer' : 'default',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 1.75,
                    ...(isPending && { filter: 'grayscale(0.4) saturate(0.85)', opacity: 0.78 }),
                }}
            >
                {isPending && (
                    <Box
                        sx={{
                            position: 'absolute', inset: 0, zIndex: 2,
                            display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', p: 1.25,
                            pointerEvents: 'none',
                            background: `linear-gradient(200deg, ${rgba(palette.ink, 0.05)}, ${rgba(palette.canvas, 0.12)})`,
                        }}
                    >
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.4, borderRadius: '999px', background: rgba(palette.ink, 0.82) }}>
                            <HourglassTopIcon sx={{ fontSize: 12, color: palette.amberGlow }} />
                            <Typography sx={{ color: '#fff', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                                Pending
                            </Typography>
                        </Box>
                    </Box>
                )}

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ height: 78, mx: -1.75, mt: -1.75, position: 'relative', overflow: 'hidden', background: companyCover ? `linear-gradient(180deg,${rgba(gradientStart,.02)},${rgba(gradientStart,.16)}),url("${companyCover}") center/cover no-repeat` : `linear-gradient(118deg,${gradientStart},${gradientEnd})`, borderBottom: `1px solid ${rgba(gradientStart,.12)}` }}>
                        {!companyCover && <PeopleIcon sx={{ position:'absolute', right:8, bottom:-20, fontSize:72, color:'rgba(255,255,255,.14)', transform:'rotate(-10deg)' }} />}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.1, mt: -2.45 }}>
                        <Box sx={{ position: 'relative', flexShrink: 0 }}>
                            <Avatar
                                src={photoURL}
                                sx={{
                                    background: `linear-gradient(150deg, ${gradientStart}, ${gradientEnd})`,
                                    color: '#fff', width: 50, height: 50, borderRadius: '13px',
                                    fontWeight: 700, fontFamily: fontDisplay, fontSize: '1.1rem',
                                    boxShadow: `0 8px 20px ${rgba(gradientStart, 0.3)}`,
                                    border: '3px solid #fff', flexShrink: 0,
                                }}
                            >
                                {!photoURL && initials}
                            </Avatar>
                            {companyLogo && <Avatar src={companyLogo} variant="rounded" sx={{ position:'absolute', right:-7, bottom:-5, width:22, height:22, borderRadius:'6px', bgcolor:'#fff', border:'2px solid #fff', boxShadow:'0 4px 10px rgba(25,57,78,.16)', '& img':{objectFit:'contain'} }} />}
                        </Box>
                        <Box sx={{ minWidth: 0, pt: 2.2 }}>
                            <Typography sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.86rem', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {displayName}
                            </Typography>
                            <Typography sx={{ fontSize: '0.64rem', color: palette.inkLight, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {role}{user.companyName ? ` · ${user.companyName}` : ''}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        {user.country && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: palette.inkLight, mb: 0.5 }}>
                                <LocationIcon sx={{ fontSize: 12 }} />
                                <Typography sx={{ fontSize: '0.66rem' }}>{user.country}</Typography>
                            </Box>
                        )}
                        {user.email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: palette.inkLight }}>
                                <EmailIcon sx={{ fontSize: 12 }} />
                                <Typography sx={{ fontSize: '0.64rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</Typography>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: `1px solid ${palette.hairlineSoft}`, gap: 1 }}>
                        {(isPending || isConnected) ? <IntroStatusPill status={introStatus} /> : <Box />}
                        {isConnected ? (
                            <Tooltip title="Connected">
                                <Box sx={{ width: 26, height: 26, display: 'grid', placeItems: 'center', borderRadius: '8px', color: palette.pine, background: rgba(palette.pine, 0.1), border: `1px solid ${rgba(palette.pine, 0.16)}` }}>
                                    <CheckCircleIcon sx={{ fontSize: 13 }} />
                                </Box>
                            </Tooltip>
                        ) : isPending ? (
                            <Tooltip title="Waiting for them to accept your intro">
                                <span>
                                    <IconButton size="small" disabled sx={{ width: 26, height: 26, borderRadius: '8px', color: palette.inkLight, background: rgba(palette.inkLight, 0.08), border: `1px solid ${rgba(palette.inkLight, 0.14)}` }}>
                                        <HourglassTopIcon sx={{ fontSize: 13 }} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        ) : (
                            <Tooltip title="Send AI Intro">
                                <IconButton
                                    size="small"
                                    sx={{ width: 26, height: 26, borderRadius: '8px', color: gradientStart, background: `linear-gradient(150deg, ${rgba(gradientStart, 0.08)}, ${rgba(gradientStart, 0.02)})`, border: `1px solid ${rgba(gradientStart, 0.12)}`, '&:hover': { transform: 'scale(1.1)', background: rgba(gradientStart, 0.12) } }}
                                    onClick={(e) => { e.stopPropagation(); onSendIntro?.(user); }}
                                >
                                    <PeopleIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </Paper>
        </motion.div>
    );
};

/* ── Floating Invites & Requests Panel ────────────────────────────────────── */
/*
   A second floating control (bottom-left, so it doesn't collide with the
   Support FAB on the right) surfacing pending company invitations and
   meeting requests without having to leave the dashboard. Invitations can
   be accepted/declined right there; meeting requests deep-link to the full
   Meetings page where the accept/decline flow already lives.
*/

const FloatingInvitesPanel = ({
    invitations,
    meetingRequests,
    onAcceptInvitation,
    onDeclineInvitation,
    onNavigateMeetings,
    processing,
}) => {
    const [open, setOpen] = useState(false);
    const total = invitations.length + meetingRequests.length;

    if (total === 0) return null;

    return (
        <Box sx={{ position: 'fixed', bottom: { xs: 16, md: 24 }, left: { xs: 16, md: 24 }, zIndex: 1000 }}>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 16, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 16, scale: 0.95 }}
                        transition={{ duration: 0.25 }}
                        style={{ position: 'absolute', bottom: 56, left: 0, width: 320, maxWidth: '86vw' }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                ...liquid(palette.teal, { radius: 20 }),
                                p: 1.75,
                                maxHeight: 400,
                                overflowY: 'auto',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '0.92rem', color: palette.ink }}>
                                    Invites &amp; Requests
                                </Typography>
                                <IconButton size="small" onClick={() => setOpen(false)} sx={{ width: 24, height: 24, color: palette.inkLight }}>
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Box>

                            {invitations.length > 0 && (
                                <Box sx={{ mb: meetingRequests.length > 0 ? 1.25 : 0 }}>
                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: palette.inkLight, mb: 0.6 }}>
                                        Company Invites
                                    </Typography>
                                    {invitations.map((inv) => (
                                        <Box
                                            key={inv.id}
                                            sx={{
                                                ...glassWell(12),
                                                p: 1,
                                                mb: 0.6,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: palette.ink }} noWrap>
                                                    {inv.companyName || 'A company'}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.6rem', color: palette.inkLight }}>
                                                    wants to connect
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                size="small"
                                                disabled={processing}
                                                onClick={() => onAcceptInvitation(inv)}
                                                sx={{ width: 26, height: 26, borderRadius: '8px', color: palette.pine, background: rgba(palette.pine, 0.1) }}
                                            >
                                                <CheckIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                disabled={processing}
                                                onClick={() => onDeclineInvitation(inv)}
                                                sx={{ width: 26, height: 26, borderRadius: '8px', color: palette.danger, background: rgba(palette.danger, 0.1) }}
                                            >
                                                <CloseIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            {meetingRequests.length > 0 && (
                                <Box>
                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: palette.inkLight, mb: 0.6 }}>
                                        Meeting Requests
                                    </Typography>
                                    {meetingRequests.map((meeting) => (
                                        <Box
                                            key={meeting.id}
                                            onClick={onNavigateMeetings}
                                            sx={{
                                                ...glassWell(12),
                                                p: 1,
                                                mb: 0.6,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: palette.ink }} noWrap>
                                                    {meeting.requesterName || meeting.serviceCategory || 'Meeting request'}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.6rem', color: palette.inkLight }}>
                                                    {meeting.scheduledTime ? formatDateTime(meeting.scheduledTime) : 'Date TBD'}
                                                </Typography>
                                            </Box>
                                            <ArrowForwardIcon sx={{ fontSize: 14, color: palette.teal }} />
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Paper>
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5, type: 'spring', stiffness: 240, damping: 20 }}
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.96 }}
            >
                <Badge
                    badgeContent={total}
                    sx={{
                        '& .MuiBadge-badge': {
                            fontFamily: fontMono, fontSize: '0.58rem', fontWeight: 700,
                            height: 20, minWidth: 20, borderRadius: '10px', color: '#fff',
                            background: `linear-gradient(140deg, ${palette.amber}, ${palette.amberGlow})`,
                            border: `2px solid ${rgba('#FFFFFF', 0.9)}`,
                            boxShadow: `0 6px 16px -4px ${rgba(palette.amber, 0.8)}`,
                            animation: 'tfPulse 2s ease-in-out infinite',
                        },
                    }}
                >
                    <Fab
                        variant="extended"
                        onClick={() => setOpen((v) => !v)}
                        sx={{
                            px: 2, height: 44, borderRadius: '16px', textTransform: 'none',
                            color: '#fff', fontFamily: fontBody, fontWeight: 600, fontSize: '0.78rem',
                            background: `linear-gradient(140deg, ${palette.teal}, ${palette.tealGlow})`,
                            border: `1px solid ${rgba('#FFFFFF', 0.28)}`,
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.4)}, 0 14px 34px -12px ${rgba(palette.teal, 0.9)}`,
                            '&:hover': { background: `linear-gradient(140deg, ${palette.tealGlow}, ${palette.teal})` },
                        }}
                    >
                        <InboxIcon sx={{ mr: 1, fontSize: 18 }} />
                        Invites
                    </Fab>
                </Badge>
            </motion.div>
        </Box>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   5 · DASHBOARD
   ═══════════════════════════════════════════════════════════════════════════ */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const CompanyDashboard = () => {
    const navigate = useNavigate();
    const { currentUser, userData, companyData } = useAuth();

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    // Core state — includes the four new items requested: totalUsers,
    // connectedUsers, meetingInvitations (meeting requests received), and
    // pendingInvitations doubling as "new invitations" (company requests received).
    const [stats, setStats] = useState({
        totalMeetings: 0,
        pendingMeetings: 0,
        completedMeetings: 0,
        totalConnections: 0,
        pendingInvitations: 0,
        meetingInvitations: 0,
        totalUsers: 0,
        connectedUsers: 0,
    });
    const [companies, setCompanies] = useState([]);
    const [upcomingMeetings, setUpcomingMeetings] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [monthlyData, setMonthlyData] = useState({
        meetings: Array(12).fill(0),
        connections: Array(12).fill(0),
    });
    const [activeAds, setActiveAds] = useState([]);

    // AI-intro tracking: { [targetCompanyId]: { status: 'pending'|'accepted'|'declined', id } }
    // Sourced from invitations WE (this company) sent — drives the pending/disabled
    // state on listing cards and gates Message/Schedule in the detail dialog.
    const [sentIntros, setSentIntros] = useState({});

    // Raw pending items for the floating "Invites & Requests" panel — kept
    // separate from the plain counts above since the panel needs to list
    // (and act on) the actual docs, not just a number.
    const [receivedInvitations, setReceivedInvitations] = useState([]);
    const [receivedMeetingRequests, setReceivedMeetingRequests] = useState([]);
    const [floatingPanelOpen, setFloatingPanelOpen] = useState(false);
    const [processingFloatingItem, setProcessingFloatingItem] = useState(false);

    // "All Users" tab (last section) — every user on the platform (not
    // scoped to this company's own team), plus the AI-intro status we've
    // sent them (mirrors `sentIntros` but for people).
    const [companiesTab, setCompaniesTab] = useState(0);
    const [platformUsers, setPlatformUsers] = useState([]);
    const [sentUserIntros, setSentUserIntros] = useState({});

    // Dialog state
    const [openNotificationDialog, setOpenNotificationDialog] = useState(false);
    const [openCompanyDetailDialog, setOpenCompanyDetailDialog] = useState(false);
    const [selectedCompanyDetail, setSelectedCompanyDetail] = useState(null);
    const [processingNotification, setProcessingNotification] = useState(false);
    const [sendingIntro, setSendingIntro] = useState(false);

    const isFirstMeetingLoad = useRef(true);
    const isFirstInvLoad = useRef(true);

    const getCompanyId = useCallback(() => {
        return companyData?.companyId || userData?.companyId || companyData?.id || userData?.id || currentUser?.uid;
    }, [companyData, userData, currentUser]);

    const getCompanyName = useCallback(() => {
        return (
            companyData?.companyName ||
            userData?.companyName ||
            companyData?.name ||
            userData?.displayName ||
            userData?.name ||
            currentUser?.displayName ||
            'User'
        );
    }, [companyData, userData, currentUser]);

    /* ── custom toast popup ────────────────────────────────────────────────── */

    const showDashboardPopup = useCallback((title, message, icon, color) => {
        toast.custom(
            (t) => (
                <Fade in={t.visible}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 1.75,
                            minWidth: 280,
                            maxWidth: 360,
                            ...liquid(color, { radius: 18 }),
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            cursor: 'pointer',
                        }}
                        onClick={() => {
                            toast.dismiss(t.id);
                            navigate('/dashboard/meetings');
                        }}
                    >
                        <Box
                            sx={{
                                width: 38,
                                height: 38,
                                borderRadius: '10px',
                                display: 'grid',
                                placeItems: 'center',
                                color: '#fff',
                                background: `linear-gradient(150deg, ${rgba(color, 0.9)}, ${rgba(color, 0.6)})`,
                                boxShadow: `0 4px 12px ${rgba(color, 0.3)}`,
                                flexShrink: 0,
                            }}
                        >
                            {icon}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 600, color: palette.ink, mb: 0.15, fontSize: '0.8rem' }}>
                                {title}
                            </Typography>
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.7rem', lineHeight: 1.35 }}>
                                {message}
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                toast.dismiss(t.id);
                            }}
                            sx={{ flexShrink: 0 }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Paper>
                </Fade>
            ),
            { duration: 6000, position: 'top-right' }
        );
    }, [navigate]);

    /* ── notification handlers ────────────────────────────────────────────── */

    const fetchNotifications = useCallback(async () => {
        const userId = currentUser?.uid;
        if (!userId) return;

        try {
            const notifRef = collection(db, 'notifications');
            const notifQuery = query(notifRef, where('userId', '==', userId));
            const snapshot = await getDocs(notifQuery);
            const notifs = [];
            snapshot.forEach((docSnap) => {
                if (docSnap.id !== '_schema' && docSnap.id !== '_placeholder') {
                    notifs.push({ id: docSnap.id, ...docSnap.data() });
                }
            });

            notifs.sort((a, b) => {
                const timeA = a.createdAt?.toDate?.() || new Date(0);
                const timeB = b.createdAt?.toDate?.() || new Date(0);
                return timeB - timeA;
            });

            const limited = notifs.slice(0, 5);
            setNotifications(
                limited.map((n) => ({
                    ...n,
                    time: n.createdAt?.toDate?.() || new Date(),
                    message: n.body || n.message,
                }))
            );
            setUnreadCount(limited.filter((n) => !n.read).length);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    }, [currentUser]);

    const handleAcceptNotification = useCallback(async (notification) => {
        if (processingNotification) return;
        setProcessingNotification(true);

        try {
            if (notification.type === 'meeting_request') {
                const meetingId = notification.meetingId
                    || notification.meetingData?.meetingId
                    || notification.data?.meetingId
                    || notification.data?.meetingData?.meetingId;
                if (!meetingId) {
                    toast.error('This meeting notification is missing its meeting reference.');
                    return;
                }

                const meetingRef = doc(db, 'meetings', meetingId);
                const meetingDoc = await getDoc(meetingRef);
                if (!meetingDoc.exists()) {
                    toast.error('This meeting no longer exists or has already been removed.');
                    return;
                }

                await updateDoc(meetingRef, {
                    status: 'confirmed',
                    confirmedAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                });
                await updateDoc(doc(db, 'notifications', notification.id), { read: true });
                toast.success('✅ Meeting accepted!');
            } else if (notification.type === 'invitation') {
                const invitationId = notification.invitationId || notification.data?.invitationId || notification.data?.id || notification.id;
                const invitationRef = doc(db, 'invitations', invitationId);
                const invitationDoc = await getDoc(invitationRef);

                if (!invitationDoc.exists()) {
                    toast.error('This invitation no longer exists or has already been processed.');
                    return;
                }

                await updateDoc(invitationRef, {
                    status: 'accepted',
                    updatedAt: serverTimestamp(),
                });
                await updateDoc(doc(db, 'notifications', notification.id), { read: true });
                toast.success('✅ Invitation accepted!');
            } else {
                const notifRef = doc(db, 'notifications', notification.id);
                await updateDoc(notifRef, { read: true });
                toast.success(`✅ ${notification.title} acknowledged`);
            }

            fetchNotifications();
        } catch (error) {
            console.error('Error accepting notification:', error);
            toast.error(friendlyError(error, 'Failed to accept notification.'));
        } finally {
            setProcessingNotification(false);
        }
    }, [fetchNotifications, processingNotification]);

    const handleDeclineNotification = useCallback(async (notification) => {
        if (processingNotification) return;
        setProcessingNotification(true);

        try {
            if (notification.type === 'meeting_request') {
                const meetingId = notification.meetingId
                    || notification.meetingData?.meetingId
                    || notification.data?.meetingId
                    || notification.data?.meetingData?.meetingId;
                if (!meetingId) {
                    toast.error('This meeting notification is missing its meeting reference.');
                    return;
                }
                const meetingRef = doc(db, 'meetings', meetingId);
                const meetingDoc = await getDoc(meetingRef);
                if (!meetingDoc.exists()) {
                    toast.error('This meeting no longer exists or has already been removed.');
                    return;
                }
                await updateDoc(meetingRef, {
                    status: 'declined',
                    declinedAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                });
                await updateDoc(doc(db, 'notifications', notification.id), { read: true });
                toast('❌ Meeting declined', { icon: '👋' });
            } else if (notification.type === 'invitation') {
                const invitationId = notification.invitationId || notification.data?.invitationId || notification.data?.id || notification.id;
                const invitationRef = doc(db, 'invitations', invitationId);
                const invitationDoc = await getDoc(invitationRef);
                if (!invitationDoc.exists()) {
                    toast.error('This invitation no longer exists or has already been processed.');
                    return;
                }
                await updateDoc(invitationRef, {
                    status: 'declined',
                    updatedAt: serverTimestamp(),
                });
                await updateDoc(doc(db, 'notifications', notification.id), { read: true });
                toast('❌ Invitation declined', { icon: '👋' });
            } else {
                const notifRef = doc(db, 'notifications', notification.id);
                await updateDoc(notifRef, { read: true });
                toast('📌 ' + notification.title + ' dismissed', { icon: 'ℹ️' });
            }

            fetchNotifications();
        } catch (error) {
            console.error('Error declining notification:', error);
            toast.error(friendlyError(error, 'Failed to decline notification.'));
        } finally {
            setProcessingNotification(false);
        }
    }, [fetchNotifications, processingNotification]);

    const handleViewNotification = useCallback(async (notification) => {
        setOpenNotificationDialog(true);
        if (!notification.read) {
            try {
                await updateDoc(doc(db, 'notifications', notification.id), { read: true });
            } catch (err) {
                console.error('Failed to update notification', err);
            }
        }
    }, []);

    const handleChatFromNotification = useCallback((notification) => {
        const companyId = notification.data?.companyId || notification.data?.targetCompanyId;
        if (companyId) {
            navigate('/dashboard/messages', {
                state: {
                    companyId: companyId,
                    companyName: notification.data?.companyName || notification.data?.targetCompanyName,
                    openChat: true,
                },
            });
        } else {
            navigate('/dashboard/messages');
        }
        setOpenNotificationDialog(false);
    }, [navigate]);

    const handleCompanyClick = useCallback((company) => {
        setSelectedCompanyDetail(company);
        setOpenCompanyDetailDialog(true);
    }, []);

    const handleNavigateToCompanyProfile = useCallback((company) => {
        setOpenCompanyDetailDialog(false);
        navigate(`/dashboard/company/${company.id}`, { state: { company } });
    }, [navigate]);

    /* ── AI intro (Connect) handler ───────────────────────────────────────── */
    /*
       Creates the `invitations` doc that the rest of the app (notifications,
       the recipient's accept/decline flow) already understands. Until the
       recipient flips its status to 'accepted', `sentIntros[company.id]`
       stays 'pending' — which is what dims the card and locks Message /
       Schedule in the detail dialog below.
    */
    const handleSendAiIntro = useCallback(async (company) => {
        const companyId = getCompanyId();
        if (!companyId || !company?.id) return;

        const existingStatus = sentIntros[company.id]?.status;
        if (existingStatus === 'pending' || existingStatus === 'accepted') {
            toast('An intro with this company is already ' + existingStatus, { icon: 'ℹ️' });
            return;
        }

        setSendingIntro(true);
        try {
            await addDoc(collection(db, 'invitations'), {
                companyId,
                companyName: getCompanyName(),
                targetCompanyId: company.id,
                targetCompanyName: company.companyName || 'Company',
                type: 'ai_intro',
                status: 'pending',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            toast.success(`AI intro sent to ${company.companyName || 'company'}`, { icon: '🤝' });
        } catch (error) {
            console.error('Error sending AI intro:', error);
            toast.error(friendlyError(error, 'Failed to send AI intro.'));
        } finally {
            setSendingIntro(false);
        }
    }, [getCompanyId, getCompanyName, sentIntros]);

    /* ── floating panel: quick accept/decline on the raw invitation doc ─────── */

    const handleAcceptInvitationDirect = useCallback(async (invitation) => {
        if (processingFloatingItem) return;
        setProcessingFloatingItem(true);
        try {
            await updateDoc(doc(db, 'invitations', invitation.id), {
                status: 'accepted',
                updatedAt: serverTimestamp(),
            });
            toast.success(`Connected with ${invitation.companyName || 'company'}!`);
        } catch (error) {
            console.error('Error accepting invitation:', error);
            toast.error(friendlyError(error, 'Failed to accept invitation.'));
        } finally {
            setProcessingFloatingItem(false);
        }
    }, [processingFloatingItem]);

    const handleDeclineInvitationDirect = useCallback(async (invitation) => {
        if (processingFloatingItem) return;
        setProcessingFloatingItem(true);
        try {
            await updateDoc(doc(db, 'invitations', invitation.id), {
                status: 'declined',
                updatedAt: serverTimestamp(),
            });
            toast('Invitation declined', { icon: '👋' });
        } catch (error) {
            console.error('Error declining invitation:', error);
            toast.error(friendlyError(error, 'Failed to decline invitation.'));
        } finally {
            setProcessingFloatingItem(false);
        }
    }, [processingFloatingItem]);

    /* ── All Users tab: send a person-to-person AI intro ────────────────── */

    const handleSendUserIntro = useCallback(async (user) => {
        const companyId = getCompanyId();
        const targetId = user.uid || user.id;
        if (!companyId || !targetId) return;

        const existingStatus = sentUserIntros[targetId]?.status;
        if (existingStatus === 'pending' || existingStatus === 'accepted') {
            toast('An intro with this person is already ' + existingStatus, { icon: 'ℹ️' });
            return;
        }

        try {
            await addDoc(collection(db, 'introductions'), {
                fromUserId: companyId,
                fromUserName: getCompanyName(),
                toUserId: targetId,
                toUserName: user.displayName || user.firstName || user.name || 'User',
                status: 'pending',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            toast.success(`Intro sent to ${user.displayName || user.firstName || 'user'}`, { icon: '🤝' });
        } catch (error) {
            console.error('Error sending user intro:', error);
            toast.error(friendlyError(error, 'Failed to send intro.'));
        }
    }, [getCompanyId, getCompanyName, sentUserIntros]);

    /* ── chart data ────────────────────────────────────────────────────────── */

    const lineChartData = useMemo(() => ({
        labels: MONTHS,
        datasets: [
            {
                label: 'Meetings',
                data: monthlyData.meetings,
                borderColor: palette.rust,
                backgroundColor: (ctx) => {
                    const { chart } = ctx;
                    if (!chart.chartArea) return rgba(palette.rust, 0.12);
                    const g = chart.ctx.createLinearGradient(0, chart.chartArea.top, 0, chart.chartArea.bottom);
                    g.addColorStop(0, rgba(palette.rust, 0.32));
                    g.addColorStop(1, rgba(palette.rust, 0));
                    return g;
                },
                fill: true,
                tension: 0.42,
                pointBackgroundColor: palette.rust,
                pointBorderColor: '#fff',
                pointBorderWidth: 2.5,
                pointRadius: 3.5,
                pointHoverRadius: 6,
                borderWidth: 2.5,
            },
            {
                label: 'Connections',
                data: monthlyData.connections,
                borderColor: palette.pine,
                backgroundColor: (ctx) => {
                    const { chart } = ctx;
                    if (!chart.chartArea) return rgba(palette.pine, 0.12);
                    const g = chart.ctx.createLinearGradient(0, chart.chartArea.top, 0, chart.chartArea.bottom);
                    g.addColorStop(0, rgba(palette.pine, 0.28));
                    g.addColorStop(1, rgba(palette.pine, 0));
                    return g;
                },
                fill: true,
                tension: 0.42,
                pointBackgroundColor: palette.pine,
                pointBorderColor: '#fff',
                pointBorderWidth: 2.5,
                pointRadius: 3.5,
                pointHoverRadius: 6,
                borderWidth: 2.5,
            },
        ],
    }), [monthlyData]);

    const barChartData = useMemo(() => ({
        labels: MONTHS,
        datasets: [{
            label: 'Monthly Activity',
            data: monthlyData.meetings.map((v, i) => v + monthlyData.connections[i]),
            backgroundColor: monthlyData.meetings.map((_, i) => {
                const colors = [palette.rust, palette.pine, palette.teal, palette.amber, palette.plum];
                return rgba(colors[i % colors.length], 0.6);
            }),
            hoverBackgroundColor: monthlyData.meetings.map((_, i) => {
                const colors = [palette.rust, palette.pine, palette.teal, palette.amber, palette.plum];
                return colors[i % colors.length];
            }),
            borderRadius: 8,
            borderSkipped: false,
            barPercentage: 0.6,
        }],
    }), [monthlyData]);

    const doughnutData = useMemo(() => ({
        labels: ['Completed', 'Pending', 'Scheduled'],
        datasets: [{
            data: [
                stats.completedMeetings || 1,
                stats.pendingMeetings || 1,
                Math.max(0, (stats.totalMeetings || 3) - (stats.completedMeetings || 1) - (stats.pendingMeetings || 1)),
            ],
            backgroundColor: [palette.pine, palette.amber, palette.teal],
            hoverOffset: 8,
            borderColor: palette.paper,
            borderWidth: 3,
        }],
    }), [stats]);

    /* ── chart options ────────────────────────────────────────────────────── */

    const baseTooltip = useMemo(() => ({
        backgroundColor: palette.ink,
        titleFont: { family: fontBody, weight: 700, size: 11 },
        bodyFont: { family: fontMono, size: 10 },
        padding: 10,
        cornerRadius: 10,
        displayColors: true,
        boxPadding: 4,
    }), []);

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        animation: { duration: 900, easing: 'easeOutQuart' },
        plugins: {
            tooltip: baseTooltip,
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 14,
                    color: palette.inkLight,
                    font: { family: fontBody, size: 10, weight: 600 },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: { color: rgba(palette.ink, 0.05), drawBorder: false },
                ticks: { color: palette.inkLight, font: { family: fontMono, size: 9 }, stepSize: 1 },
            },
            x: {
                grid: { display: false },
                ticks: { color: palette.inkLight, font: { family: fontMono, size: 9 } },
            },
        },
    }), [baseTooltip]);

    const doughnutOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: { animateRotate: true, duration: 1000, easing: 'easeOutQuart' },
        plugins: {
            tooltip: baseTooltip,
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 12,
                    color: palette.inkLight,
                    font: { family: fontBody, size: 10, weight: 600 },
                },
            },
        },
        cutout: '62%',
    }), [baseTooltip]);

    /* ── real-time listeners ──────────────────────────────────────────────── */

    useEffect(() => {
        const companyId = getCompanyId();
        const userId = currentUser?.uid;

        if (!companyId) {
            setLoading(false);
            return;
        }

        setLoading(true);

        // 1. Meetings
        const meetingsRef = collection(db, 'meetings');
        const meetingsQuery = query(
            meetingsRef,
            or(where('requesterId', '==', companyId), where('targetId', '==', companyId))
        );

        const unsubMeetings = onSnapshot(
            meetingsQuery,
            (snapshot) => {
                let total = 0;
                let completed = 0;
                let pending = 0;
                let receivedPending = 0; // "Meeting Invites" — meeting requests sent TO us, awaiting our response
                const upcoming = [];
                const receivedMeetingReqs = [];
                const monthlyMeetingsArr = Array(12).fill(0);

                if (!isFirstMeetingLoad.current) {
                    snapshot.docChanges().forEach((change) => {
                        const data = change.doc.data();
                        if (change.doc.id === '_schema' || change.doc.id === '_placeholder') return;

                        if (change.type === 'added' && data.targetId === companyId && data.status === 'pending') {
                            showDashboardPopup(
                                'New Meeting Request',
                                `${data.requesterName || 'A company'} wants to schedule a meeting with you.`,
                                <EventIcon fontSize="small" />,
                                palette.rust
                            );
                        }

                        if (change.type === 'modified' && data.requesterId === companyId && (data.status === 'confirmed' || data.status === 'accepted')) {
                            showDashboardPopup(
                                'Meeting Accepted!',
                                `${data.targetCompany || 'A company'} accepted your meeting request.`,
                                <CheckCircleIcon fontSize="small" />,
                                palette.pine
                            );
                        }
                    });
                }
                isFirstMeetingLoad.current = false;

                snapshot.forEach((docSnap) => {
                    if (docSnap.id === '_schema' || docSnap.id === '_placeholder') return;
                    const data = docSnap.data();
                    total++;

                    if (data.status === 'completed') completed++;
                    if (data.status === 'pending' || data.status === 'scheduled' || data.status === 'confirmed') {
                        pending++;
                        upcoming.push({ id: docSnap.id, ...data });
                    }
                    if (data.status === 'pending' && data.targetId === companyId) {
                        receivedPending++;
                    }
                    // "Latest Meeting Requests" panel: every meeting touching
                    // this company, any direction/status — mirrors the
                    // invitations panel below so it isn't empty just because
                    // this company mostly *sends* meeting requests.
                    if (data.requesterId === companyId || data.targetId === companyId) {
                        receivedMeetingReqs.push({
                            id: docSnap.id,
                            ...data,
                            _isReceiver: data.targetId === companyId,
                        });
                    }

                    if (data.createdAt) {
                        const date = toDate(data.createdAt);
                        if (date) {
                            monthlyMeetingsArr[date.getMonth()]++;
                        }
                    }
                });

                upcoming.sort((a, b) => {
                    const timeA = toDate(a.scheduledTime) || new Date(0);
                    const timeB = toDate(b.scheduledTime) || new Date(0);
                    return timeA - timeB;
                });

                receivedMeetingReqs.sort((a, b) => {
                    const timeA = toDate(a.updatedAt) || toDate(a.createdAt) || new Date(0);
                    const timeB = toDate(b.updatedAt) || toDate(b.createdAt) || new Date(0);
                    return timeB - timeA;
                });

                setStats((prev) => ({
                    ...prev,
                    totalMeetings: total,
                    completedMeetings: completed,
                    pendingMeetings: pending,
                    meetingInvitations: receivedPending,
                }));
                setUpcomingMeetings(upcoming.slice(0, 4));
                setReceivedMeetingRequests(receivedMeetingReqs.slice(0, 5));
                setMonthlyData((prev) => ({ ...prev, meetings: monthlyMeetingsArr }));
                setLoading(false);
            },
            (err) => {
                console.error('Error fetching meetings:', err);
                setLoading(false);
            }
        );

        // 2. Companies
        const companiesRef = collection(db, 'companies');
        const unsubCompanies = onSnapshot(companiesRef, (snapshot) => {
            const comps = [];
            snapshot.forEach((docSnap) => {
                const data = docSnap.data();
                if (docSnap.id !== '_schema' && docSnap.id !== '_placeholder' && docSnap.id !== companyId && data.companyId !== companyId) {
                    comps.push({ id: docSnap.id, ...data });
                }
            });
            setCompanies(comps.slice(0, 6));
        });

        // 3. Notifications
        let unsubNotifs = () => {};
        if (userId) {
            const notifRef = collection(db, 'notifications');
            const notifQuery = query(notifRef, where('userId', '==', userId));
            unsubNotifs = onSnapshot(notifQuery, (snapshot) => {
                const notifs = [];
                snapshot.forEach((docSnap) => {
                    if (docSnap.id !== '_schema' && docSnap.id !== '_placeholder') {
                        notifs.push({ id: docSnap.id, ...docSnap.data() });
                    }
                });

                notifs.sort((a, b) => {
                    const timeA = a.createdAt?.toDate?.() || new Date(0);
                    const timeB = b.createdAt?.toDate?.() || new Date(0);
                    return timeB - timeA;
                });

                const limited = notifs.slice(0, 5);
                setNotifications(
                    limited.map((n) => ({
                        ...n,
                        time: n.createdAt?.toDate?.() || new Date(),
                        message: n.body || n.message,
                    }))
                );
                setUnreadCount(limited.filter((n) => !n.read).length);
            });
        }

        // 4. Invitations / Connections (also feeds `sentIntros` for the listing UI)
        const invRef = collection(db, 'invitations');
        const invQuery = query(invRef, or(where('companyId', '==', companyId), where('targetCompanyId', '==', companyId)));
        const unsubInvites = onSnapshot(invQuery, (snapshot) => {
            let totalConn = 0;
            let pendingInv = 0;
            const monthlyConn = Array(12).fill(0);
            const sentMap = {};
            const receivedInvs = [];

            if (!isFirstInvLoad.current) {
                snapshot.docChanges().forEach((change) => {
                    const data = change.doc.data();
                    if (change.doc.id === '_schema' || change.doc.id === '_placeholder') return;

                    if (change.type === 'added' && data.targetCompanyId === companyId && data.status === 'pending') {
                        showDashboardPopup(
                            'New Connection Request',
                            `${data.companyName || 'A company'} wants to connect with you.`,
                            <PeopleIcon fontSize="small" />,
                            palette.amber
                        );
                    }
                    if (change.type === 'modified' && data.companyId === companyId && (data.status === 'accepted' || data.status === 'connected')) {
                        showDashboardPopup(
                            'Connection Accepted',
                            `You are now connected with ${data.targetCompanyName || 'a company'}.`,
                            <VerifiedIcon fontSize="small" />,
                            palette.teal
                        );
                    }
                });
            }
            isFirstInvLoad.current = false;

            snapshot.forEach((docSnap) => {
                if (docSnap.id === '_schema' || docSnap.id === '_placeholder') return;
                const data = docSnap.data();
                if ((data.status === 'accepted' || data.status === 'connected')) {
                    totalConn++;
                    if (data.createdAt) {
                        const date = toDate(data.createdAt);
                        if (date) {
                            monthlyConn[date.getMonth()]++;
                        }
                    }
                }
                if (data.status === 'pending' && data.targetCompanyId === companyId) {
                    pendingInv++;
                }

                // "Latest Invitations" panel: every invitation touching this
                // company, any direction/status — not just pending-received —
                // so the panel has something to show even when this company
                // mostly *sends* intros rather than receiving them.
                if (data.companyId === companyId || data.targetCompanyId === companyId) {
                    receivedInvs.push({
                        id: docSnap.id,
                        ...data,
                        _isReceiver: data.targetCompanyId === companyId,
                    });
                }

                // Build the "intros WE sent" map — this is what the listing cards
                // and the detail dialog's Message/Schedule gate read from.
                if (data.companyId === companyId && data.targetCompanyId) {
                    const existing = sentMap[data.targetCompanyId];
                    const existingDate = existing ? toDate(existing.updatedAt) : null;
                    const thisDate = toDate(data.updatedAt) || toDate(data.createdAt);
                    if (!existing || !existingDate || (thisDate && thisDate > existingDate)) {
                        sentMap[data.targetCompanyId] = {
                            status: data.status,
                            id: docSnap.id,
                            updatedAt: data.updatedAt || data.createdAt,
                        };
                    }
                }
            });

            receivedInvs.sort((a, b) => {
                const timeA = toDate(a.updatedAt) || toDate(a.createdAt) || new Date(0);
                const timeB = toDate(b.updatedAt) || toDate(b.createdAt) || new Date(0);
                return timeB - timeA;
            });

            setStats((prev) => ({ ...prev, totalConnections: totalConn, pendingInvitations: pendingInv }));
            setMonthlyData((prev) => ({ ...prev, connections: monthlyConn }));
            setSentIntros(sentMap);
            setReceivedInvitations(receivedInvs.slice(0, 5));
        });

        // 5. Active Ads - limit to 2
        const adsRef = collection(db, 'ads');
        const adsQuery = query(adsRef, where('isActive', '==', true), limit(2));
        const unsubAds = onSnapshot(adsQuery, (snapshot) => {
            const adsList = [];
            snapshot.forEach((docSnap) => {
                if (docSnap.id !== '_schema' && docSnap.id !== '_placeholder') {
                    adsList.push({ id: docSnap.id, ...docSnap.data() });
                }
            });
            setActiveAds(adsList);
        });

        // 6. Platform-wide user count (Total Users stat) — a lightweight one-time
        // count rather than a live listener, since this number doesn't need to
        // update every second and a full 'users' snapshot would be expensive.
        (async () => {
            try {
                const usersRef = collection(db, 'users');
                const usersCountSnap = await getCountFromServer(usersRef);
                const rawTotal = usersCountSnap.data().count || 0;
                // Exclude the current user from the platform-wide total so the
                // number reflects "other people on the platform".
                setStats((prev) => ({ ...prev, totalUsers: Math.max(0, rawTotal - 1) }));
            } catch (err) {
                console.error('Error counting users:', err);
            }
        })();

        // 7. Connected users — AI introductions (person-to-person) that have been
        // accepted, counted from either direction (we sent it, or we received it).
        const introRef = collection(db, 'introductions');
        const introQuery = query(introRef, or(where('fromUserId', '==', companyId), where('toUserId', '==', companyId)));
        const unsubIntros = onSnapshot(introQuery, (snapshot) => {
            let connectedUsers = 0;
            const userIntroMap = {};
            snapshot.forEach((docSnap) => {
                if (docSnap.id === '_schema' || docSnap.id === '_placeholder') return;
                const data = docSnap.data();
                if (data.status === 'accepted' || data.status === 'connected') connectedUsers++;
                // Track intros WE sent so the Company Users tab can grey out /
                // label a card "Pending" or "Connected" the same way company
                // cards do.
                if (data.fromUserId === companyId && data.toUserId) {
                    userIntroMap[data.toUserId] = { status: data.status, id: docSnap.id };
                }
            });
            setStats((prev) => ({ ...prev, connectedUsers }));
            setSentUserIntros(userIntroMap);
        }, (err) => {
            console.error('Error listening to introductions:', err);
        });

        // 8. All users on the platform for the "All Users" tab in the last
        // section — deliberately NOT scoped to this company's own team; the
        // only exclusion is the current user themself.
        const usersListRef = collection(db, 'users');
        const unsubUsers = onSnapshot(usersListRef, (snapshot) => {
            const list = [];
            snapshot.forEach((docSnap) => {
                if (docSnap.id === '_schema' || docSnap.id === '_placeholder') return;
                const data = docSnap.data();
                if ((data.uid || docSnap.id) === currentUser?.uid) return;
                list.push({ id: docSnap.id, ...data });
            });
            setPlatformUsers(list);
        }, (err) => {
            console.error('Error listening to users:', err);
        });

        return () => {
            unsubMeetings();
            unsubCompanies();
            unsubNotifs();
            unsubInvites();
            unsubAds();
            unsubIntros();
            unsubUsers();
        };
    }, [currentUser, companyData, userData, getCompanyId, showDashboardPopup]);

    /* ── derived data ────────────────────────────────────────────────────── */

    const statsData = useMemo(() => {
        return [
            {
                label: 'Active Connections',
                value: stats.totalConnections,
                icon: <PeopleIcon />,
                gradient: [palette.blue, palette.sky],
                hint: `${stats.pendingInvitations} pending invitation${stats.pendingInvitations === 1 ? '' : 's'}`,
            },
            {
                label: 'Meetings',
                value: stats.totalMeetings,
                icon: <EventIcon />,
                gradient: [palette.orange, palette.coral],
                hint: `${stats.pendingMeetings} awaiting action`,
            },
            {
                label: 'People Connected',
                value: stats.connectedUsers,
                icon: <HandshakeIcon />,
                gradient: [palette.green, palette.mint],
                hint: `${stats.totalUsers} people on platform`,
            },
            {
                label: 'Unread Messages',
                value: unreadCount,
                icon: <MessageIcon />,
                gradient: [palette.plum, palette.plumGlow],
                hint: unreadCount ? 'Requires your attention' : 'Inbox is up to date',
            },
        ];
    }, [stats, unreadCount, companyData?.primaryColor, companyData?.secondaryColor, companyData?.accentColor]);

    const quickActions = useMemo(() => [
        { icon: <EventIcon />, label: 'Schedule Meeting', desc: 'Book a new session', accent: palette.rust, onClick: () => navigate('/dashboard/meetings/schedule') },
        { icon: <PeopleIcon />, label: 'Find Companies', desc: 'Discover partners', accent: palette.amber, onClick: () => navigate('/dashboard/companies') },
        { icon: <MessageIcon />, label: 'Messages', desc: 'Check inbox', accent: palette.teal, badge: unreadCount, onClick: () => navigate('/dashboard/messages') },
        { icon: <BusinessIcon />, label: 'Company Profile', desc: 'Update your profile', accent: palette.pine, onClick: () => navigate('/dashboard/company') },
        { icon: <AiIcon />, label: 'AI Insights', desc: 'Smart analytics', accent: palette.plum, onClick: () => navigate('/dashboard/company/ai-insights') },
        { icon: <SupportAgentIcon />, label: 'Support Chat', desc: 'Get help', accent: palette.admin, onClick: () => navigate('/dashboard/support') },
    ], [navigate, unreadCount, companyData?.primaryColor, companyData?.secondaryColor, companyData?.accentColor]);

    // Intro status for whichever company is currently open in the detail dialog
    const selectedIntroStatus = selectedCompanyDetail ? sentIntros[selectedCompanyDetail.id]?.status : undefined;

    // The Notifications section only ever shows items that still need
    // attention — once a notification is marked read (which happens the
    // moment it's accepted/declined/viewed), it drops out of this list.
    const pendingNotifications = useMemo(() => notifications.filter((n) => !n.read), [notifications]);

    // Floating "Invites & Requests" panel total badge.
    // The floating panel is for *quick action* — only pending items this
    // company actually received (i.e. can accept/decline) belong there.
    // The two "Latest…" panels on the page show broader recent activity
    // (any status/direction) so they're never empty just because this
    // company mostly sends invites/requests rather than receiving them.
    const actionableInvitations = useMemo(
        () => receivedInvitations.filter((i) => i.status === 'pending' && i._isReceiver),
        [receivedInvitations]
    );
    const actionableMeetingRequests = useMemo(
        () => receivedMeetingRequests.filter((m) => m.status === 'pending' && m._isReceiver),
        [receivedMeetingRequests]
    );
    const floatingItemsCount = actionableInvitations.length + actionableMeetingRequests.length;

    /* ── render ────────────────────────────────────────────────────────────── */

    const chartShell = { position: 'relative', flex: 1, minHeight: 180, width: '100%' };
    const heroText = getGradientContrastText(palette.rustDeep, palette.pine);
    const heroMuted = heroText === '#FFFFFF' ? 'rgba(255,255,255,.88)' : 'rgba(23,34,53,.78)';
    const heroSubtle = heroText === '#FFFFFF' ? 'rgba(255,255,255,.76)' : 'rgba(23,34,53,.68)';
    const heroGlass = heroText === '#FFFFFF' ? 'rgba(255,255,255,.12)' : 'rgba(23,34,53,.075)';
    const heroGlassHover = heroText === '#FFFFFF' ? 'rgba(255,255,255,.20)' : 'rgba(23,34,53,.12)';
    const heroBorder = heroText === '#FFFFFF' ? 'rgba(255,255,255,.23)' : 'rgba(23,34,53,.13)';

    return (
        <Box
            className="erp-page-shell"
            sx={{
                minHeight: '100vh',
                position: 'relative',
                fontFamily: fontBody,
                color: palette.ink,
                background: palette.canvas,
                '&::before': {
                    content: '""',
                    position: 'fixed',
                    inset: 0,
                    zIndex: 0,
                    pointerEvents: 'none',
                    opacity: 0,
                    backgroundImage:
                        'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27140%27 height=%27140%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.03%27/%3E%3C/svg%3E")',
                    animation: 'tfGrain 8s steps(6) infinite',
                },
            }}
        >
            <style>{FONT_IMPORT}</style>
            <Box sx={{ position: 'relative', zIndex: 2 }}><Header /></Box>

            <Container
                maxWidth={false}
                disableGutters
                sx={{ position: 'relative', zIndex: 1, px: { xs: 1.25, sm: 2, lg: 2.5 }, py: { xs: 1.1, md: 1.35 }, maxWidth: '1780px', mx: 'auto' }}
            >
                {/* ── Screenshot-precision company command center ─────────────── */}
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .34 }}>
                    <Box
                        sx={{
                            mb: 1.45,
                            minHeight: { xs: 250, md: 220 },
                            p: { xs: 1.8, md: 2.2 },
                            borderRadius: '22px',
                            position: 'relative',
                            overflow: 'hidden',
                            color: heroText,
                            background: `linear-gradient(118deg, ${palette.rustDeep} 0%, ${palette.rust} 46%, ${palette.pine} 100%)`,
                            border: '1px solid rgba(255,255,255,.24)',
                            boxShadow: `0 18px 44px ${rgba(palette.rust, .18)}, 0 4px 12px rgba(25,57,78,.08)`,
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1.45fr) minmax(360px,.85fr)' },
                            gap: 1.7,
                            alignItems: 'center',
                            '&::before': {
                                content: '""', position: 'absolute', width: 250, height: 250, borderRadius: '50%',
                                right: -65, top: -85, background: 'rgba(255,255,255,.075)',
                            },
                            '&::after': {
                                content: '""', position: 'absolute', width: 190, height: 190, borderRadius: '50%',
                                left: '43%', bottom: -135, background: 'rgba(255,255,255,.055)',
                            },
                        }}
                    >
                        <Box sx={{ position: 'relative', zIndex: 2, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box sx={{ width: 42, height: 42, borderRadius: '11px', p: .45, bgcolor: 'rgba(255,255,255,.94)', border: '1px solid rgba(255,255,255,.6)', boxShadow: '0 7px 18px rgba(9,42,62,.16)', display: 'grid', placeItems: 'center', overflow: 'hidden' }}>
                                    {companyData?.logoUrl || companyData?.logo ? (
                                        <Box component="img" src={companyData?.logoUrl || companyData?.logo} alt="Company logo" sx={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '7px' }} />
                                    ) : <BusinessIcon sx={{ color: palette.rust, fontSize: 21 }} />}
                                </Box>
                                <Box>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .55, px: .9, height: 23, borderRadius: '999px', bgcolor: heroGlass, border: `1px solid ${heroBorder}`, fontSize: '0.53rem', fontWeight: 800, letterSpacing: '.085em', textTransform: 'uppercase' }}>
                                        <DiamondIcon sx={{ fontSize: 12 }} /> Company Command Center
                                    </Box>
                                    <Typography sx={{ mt: .4, fontSize: '0.62rem', fontWeight: 700, opacity: .88 }}>{getCompanyName()}</Typography>
                                </Box>
                            </Box>

                            <Typography sx={{ fontSize: { xs: '1.55rem', md: '2rem' }, fontWeight: 850, letterSpacing: '-.045em', lineHeight: 1.08, textShadow: '0 2px 12px rgba(10,40,55,.14)' }}>
                                Welcome back, {userData?.firstName || userData?.displayName || currentUser?.displayName || 'Administrator'}
                            </Typography>
                            <Typography sx={{ mt: .75, maxWidth: 720, fontSize: { xs: '.7rem', md: '.75rem' }, lineHeight: 1.55, color: heroMuted }}>
                                One operational workspace connecting meetings, companies, contacts, messages and AI-powered business introductions without leaving your company workflow.
                            </Typography>

                            <Box sx={{ mt: 1.1, display: 'flex', flexWrap: 'wrap', gap: .55 }}>
                                {[
                                    [<BusinessIcon />, getCompanyName()],
                                    [<CalendarIcon />, new Date().toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })],
                                    [<LiveIcon />, `${stats.totalConnections || 0} active connections`],
                                    [<GroupIcon />, `${companyData?.subscription?.planName || 'Company plan'} · ${Number(companyData?.subscription?.usedSeats || 0) + Number(companyData?.subscription?.reservedSeats || 0)}/${companyData?.subscription?.unlimitedUsers || Number(companyData?.subscription?.userLimit || companyData?.subscription?.maxUsers || 0) === 0 ? '∞' : Number(companyData?.subscription?.userLimit || companyData?.subscription?.maxUsers || 0)} seats`],
                                ].map(([icon, text], i) => (
                                    <Box key={i} sx={{ display: 'inline-flex', alignItems: 'center', gap: .45, height: 23, px: .8, borderRadius: '999px', bgcolor: heroGlass, border: `1px solid ${heroBorder}`, fontSize: '.52rem', fontWeight: 700, '& svg': { fontSize: 11 } }}>
                                        {icon}{text}
                                    </Box>
                                ))}
                            </Box>

                            <Box sx={{ mt: 1.05, display: 'flex', flexWrap: 'wrap', gap: .6 }}>
                                <Button onClick={() => navigate('/dashboard/meetings/schedule')} startIcon={<EventIcon />} sx={{ minHeight: 31, color: `${heroText} !important`, bgcolor: `${heroGlass} !important`, border: `1px solid ${heroBorder} !important`, boxShadow: 'none !important', '&:hover': { bgcolor: `${heroGlassHover} !important` } }}>Schedule Meeting</Button>
                                <Button onClick={() => navigate('/dashboard/companies')} startIcon={<HandshakeIcon />} sx={{ minHeight: 31, color: `${heroText} !important`, bgcolor: `${heroGlass} !important`, border: `1px solid ${heroBorder} !important`, boxShadow: 'none !important', '&:hover': { bgcolor: `${heroGlassHover} !important` } }}>Find Companies</Button>
                            </Box>
                        </Box>

                        <Box sx={{ position: 'relative', zIndex: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: .75 }}>
                            {statsData.map((s, i) => {
                                const accent = s.gradient?.[0] || '#fff';
                                return (
                                    <Box key={s.label} sx={{ minHeight: 70, p: .9, borderRadius: '12px', background: `linear-gradient(145deg, ${heroGlass}, ${rgba(accent,.18)})`, border: `1px solid ${heroBorder}`, boxShadow: 'inset 0 1px 0 rgba(255,255,255,.14), 0 7px 16px rgba(13,54,70,.08)', backdropFilter: 'blur(10px)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: .6 }}>
                                            <Box sx={{ width: 28, height: 28, borderRadius: '8px', display: 'grid', placeItems: 'center', bgcolor: rgba(accent,.30), color: getContrastText(accent), '& svg': { fontSize: 14 } }}>{s.icon}</Box>
                                            <Typography sx={{ fontSize: '1.05rem', fontWeight: 850, lineHeight: 1 }}>{compact(s.value)}</Typography>
                                        </Box>
                                        <Typography sx={{ mt: .55, fontSize: '.48rem', fontWeight: 750, letterSpacing: '.055em', textTransform: 'uppercase', color: heroSubtle }} noWrap>{s.label}</Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </motion.div>

                {/* ── Connected Command Centers — mirrors supplied screenshot ─────── */}
                <Box className="tf-section-surface" sx={{ mb: 1.55, p: { xs: 1.2, md: 1.35 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.05 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, minWidth: 0 }}>
                            <GlassIcon icon={<RocketIcon />} color={palette.teal} size={34} />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: '.79rem', fontWeight: 850, color: palette.ink }}>Connected Command Centers</Typography>
                                <Typography sx={{ mt: .1, fontSize: '.53rem', color: palette.inkFaint }}>Open the company tools you use most without leaving the workspace</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: { xs: 'none', sm: 'inline-flex' }, alignItems: 'center', gap: .42, px: .78, height: 21, borderRadius: '999px', color: palette.success, bgcolor: rgba(palette.success,.07), border: `1px solid ${rgba(palette.success,.10)}`, fontSize: '.48rem', fontWeight: 760 }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: palette.success }} /> 3 live workspaces
                        </Box>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3,minmax(0,1fr))' }, gap: 1 }}>
                        <CommandCenterCard
                            title="Meetings & Scheduling"
                            eyebrow="Collaboration"
                            description="Schedule sessions, manage incoming requests, join meeting rooms and keep company conversations moving."
                            icon={<EventIcon />}
                            accent={palette.blue}
                            accent2={palette.sky}
                            tags={['Schedule', 'Requests', 'Meeting Rooms']}
                            meta={`${stats.totalMeetings || 0} meetings • ${stats.pendingMeetings || 0} pending`}
                            onClick={() => navigate('/dashboard/meetings')}
                        />
                        <CommandCenterCard
                            title="Company Network"
                            eyebrow="Business Network"
                            description="Discover companies, review invitations, manage active connections and open partner profiles in one place."
                            icon={<BusinessIcon />}
                            accent={palette.purple}
                            accent2={palette.rose}
                            tags={['Directory', 'Connections', 'Invitations']}
                            meta={`${stats.totalConnections || 0} active connections`}
                            onClick={() => navigate('/dashboard/companies')}
                        />
                        <CommandCenterCard
                            title="AI & Messaging"
                            eyebrow="Smart Workspace"
                            description="Use AI matching, business introductions and direct messages to turn relevant connections into action."
                            icon={<AutoAwesomeIcon />}
                            accent={palette.teal}
                            accent2={palette.green}
                            tags={['AI Insights', 'Messages', 'People']}
                            meta={`${unreadCount || 0} unread messages`}
                            onClick={() => navigate('/dashboard/company/ai-insights')}
                        />
                    </Box>
                </Box>

                {/* ── Business Snapshot — colourful elevated cards ─────────── */}
                <Box className="tf-section-surface" sx={{ mb: 1.55, p: { xs: 1.25, md: 1.45 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.05 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: .75 }}>
                            <GlassIcon icon={<AnalyticsIcon />} color={palette.teal} size={34} />
                            <Box>
                                <Typography sx={{ fontSize: '.78rem', fontWeight: 800, color: palette.ink }}>Business Snapshot</Typography>
                                <Typography sx={{ mt: .12, fontSize: '.54rem', color: palette.inkFaint }}>Live company activity with visual context</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .45, px: .8, height: 22, borderRadius: '999px', fontSize: '.5rem', fontWeight: 750, color: palette.success, bgcolor: rgba(palette.success,.07), border: `1px solid ${rgba(palette.success,.10)}` }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: palette.success, boxShadow: `0 0 0 3px ${rgba(palette.success,.08)}` }} /> Live data
                        </Box>
                    </Box>
                    <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', xl: 'repeat(4,1fr)' } }}>
                        {statsData.map((s, i) => <ModernStatCard key={s.label} {...s} delay={i * .045} loading={loading} />)}
                    </Box>
                </Box>

                {/* ── ads banner - 2 ads across ────────────────────────────── */}
                {activeAds.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <PanelHead
                            icon={<AdIcon />}
                            title="Featured Sponsors"
                            accent={palette.amber}
                            chip="sponsored"
                            action="View All"
                            onAction={() => navigate('/dashboard/ads')}
                            dense
                        />
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                gap: 1.5,
                                width: '100%',
                            }}
                        >
                            {activeAds.map((ad) => (
                                <Box key={ad.id} sx={{ width: '100%' }}>
                                    <AdsBanner ad={ad} />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* ── charts row ────────────────────────────────────────────── */}
                <Box
                    sx={{
                        display: 'grid', gap: 1.5,
                        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(0, 1fr)' },
                        mb: 1.5,
                    }}
                >
                    <GlassPanel accent={palette.rust} delay={0.05}>
                        <PanelHead
                            icon={<AnalyticsIcon />}
                            title="Activity Trends"
                            accent={palette.rust}
                            chip="last 12 months"
                            dense
                        />
                        <Box sx={chartShell}>
                            {loading ? (
                                <Skeleton variant="rounded" height="100%" sx={{ bgcolor: palette.hairline, borderRadius: '16px' }} />
                            ) : (
                                <Line data={lineChartData} options={chartOptions} />
                            )}
                        </Box>
                    </GlassPanel>

                    <GlassPanel accent={palette.pine} delay={0.1}>
                        <PanelHead icon={<EventIcon />} title="Meeting Status" accent={palette.pine} chip="split" dense />
                        <Box sx={chartShell}>
                            {loading ? (
                                <Skeleton variant="circular" width="65%" height="65%" sx={{ bgcolor: palette.hairline, mx: 'auto' }} />
                            ) : stats.totalMeetings > 0 ? (
                                <Doughnut data={doughnutData} options={doughnutOptions} />
                            ) : (
                                <EmptyState icon={<EventIcon />} text="No meetings recorded" />
                            )}
                        </Box>
                    </GlassPanel>
                </Box>

                {/* ── performance + quick actions ──────────────────────────── */}
                <Box
                    sx={{
                        display: 'grid', gap: 1.5,
                        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.4fr) minmax(0, 1fr)' },
                        mb: 1.5,
                    }}
                >
                    <GlassPanel accent={palette.teal} delay={0.05}>
                        <PanelHead
                            icon={<TrendingUpIcon />}
                            title="Monthly Performance"
                            accent={palette.teal}
                            chip="meetings + connections"
                            dense
                        />
                        <Box sx={chartShell}>
                            {loading ? (
                                <Skeleton variant="rounded" height="100%" sx={{ bgcolor: palette.hairline, borderRadius: '16px' }} />
                            ) : (
                                <Bar data={barChartData} options={chartOptions} />
                            )}
                        </Box>
                    </GlassPanel>

                    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateRows: 'auto 1fr', minWidth: 0 }}>
                        <GlassPanel accent={palette.teal} delay={0.1} lift={false}>
                            <PanelHead icon={<RocketIcon />} title="Quick Actions" accent={palette.teal} dense />
                            <Box sx={{ display: 'grid', gap: 0.75, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr' } }}>
                                {quickActions.slice(0, 4).map((a, i) => (
                                    <QuickAction key={a.label} {...a} delay={0.1 + i * 0.04} />
                                ))}
                            </Box>
                        </GlassPanel>

                        <GlassPanel accent={palette.amber} delay={0.15}>
                            <PanelHead
                                icon={<PeopleIcon />}
                                title="Connections"
                                accent={palette.amber}
                                chip={`${stats.totalConnections} total`}
                                dense
                            />
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', py: 1 }}>
                                <Box sx={{ position: 'relative', width: 88, height: 88, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            borderRadius: '50%',
                                            border: `2px solid ${rgba(palette.amber, 0.12)}`,
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: -8,
                                            borderRadius: '50%',
                                            border: `2px solid ${rgba(palette.amber, 0.06)}`,
                                        }}
                                    />
                                    <Box sx={{ ...glassWell(38), width: 74, height: 74, display: 'grid', placeItems: 'center' }}>
                                        <Typography
                                            sx={{
                                                fontFamily: fontDisplay,
                                                fontWeight: 700,
                                                fontSize: '1.6rem',
                                                lineHeight: 1,
                                                background: `linear-gradient(135deg, ${palette.ink} 25%, ${palette.amber})`,
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                            }}
                                        >
                                            {stats.totalConnections}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Typography sx={{ color: palette.inkLight, fontSize: '0.68rem', mt: 1 }}>
                                    {stats.pendingInvitations} pending invitations
                                </Typography>
                            </Box>
                        </GlassPanel>
                    </Box>
                </Box>

                {/* ── notifications + more actions ─────────────────────────── */}
                <Box
                    sx={{
                        display: 'grid', gap: 1.5,
                        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.4fr) minmax(0, 1fr)' },
                        mb: 1.5,
                    }}
                >
                    <GlassPanel accent={palette.amber} delay={0.05}>
                        <PanelHead
                            icon={<NotificationIcon />}
                            title="Notifications"
                            accent={palette.amber}
                            chip={pendingNotifications.length > 0 ? `${pendingNotifications.length} pending` : 'all clear'}
                            action="View All"
                            onAction={() => setOpenNotificationDialog(true)}
                            dense
                        />
                        <Box sx={{ flex: 1, maxHeight: 240, overflowY: 'auto', pr: 0.5, mr: -0.5 }}>
                            {loading ? (
                                Array.from({ length: 3 }).map((_, i) => (
                                    <Skeleton key={i} variant="rounded" height={56} sx={{ mb: 0.6, borderRadius: '12px', bgcolor: palette.hairline }} />
                                ))
                            ) : pendingNotifications.length === 0 ? (
                                <EmptyState icon={<NotificationIcon />} text="Nothing pending — you're all caught up" />
                            ) : (
                                pendingNotifications.slice(0, 3).map((notification, i) => (
                                    <NotificationCard
                                        key={notification.id}
                                        notification={notification}
                                        onAccept={handleAcceptNotification}
                                        onDecline={handleDeclineNotification}
                                        onView={handleViewNotification}
                                        onChat={handleChatFromNotification}
                                    />
                                ))
                            )}
                        </Box>
                    </GlassPanel>

                    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateRows: 'auto 1fr', minWidth: 0 }}>
                        <GlassPanel accent={palette.rust} delay={0.1} lift={false}>
                            <PanelHead icon={<RocketIcon />} title="More Actions" accent={palette.rust} dense />
                            <Box sx={{ display: 'grid', gap: 0.75, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr' } }}>
                                {quickActions.slice(4).map((a, i) => (
                                    <QuickAction key={a.label} {...a} delay={0.1 + i * 0.04} />
                                ))}
                            </Box>
                        </GlassPanel>

                        <GlassPanel accent={palette.teal} delay={0.15}>
                            <PanelHead
                                icon={<EventIcon />}
                                title="Upcoming"
                                accent={palette.teal}
                                chip={`${upcomingMeetings.length} scheduled`}
                                action="View All"
                                onAction={() => navigate('/dashboard/meetings')}
                                dense
                            />
                            <Box sx={{ flex: 1, maxHeight: 240, overflowY: 'auto', pr: 0.5, mr: -0.5 }}>
                                {loading ? (
                                    Array.from({ length: 3 }).map((_, i) => (
                                        <Skeleton key={i} variant="rounded" height={44} sx={{ mb: 0.6, borderRadius: '12px', bgcolor: palette.hairline }} />
                                    ))
                                ) : upcomingMeetings.length === 0 ? (
                                    <EmptyState
                                        icon={<CalendarIcon />}
                                        text="No upcoming meetings"
                                        action="Schedule one"
                                        onAction={() => navigate('/dashboard/meetings/schedule')}
                                    />
                                ) : (
                                    upcomingMeetings.slice(0, 3).map((meeting, i) => (
                                        <LiquidRow key={meeting.id} accent={toneFor(meeting.status)} index={i} onClick={() => navigate('/dashboard/meetings')}>
                                            <Box
                                                sx={{
                                                    width: 32, flexShrink: 0, textAlign: 'center', py: 0.3, borderRadius: '9px',
                                                    background: rgba(toneFor(meeting.status), 0.08),
                                                    border: `1px solid ${rgba(toneFor(meeting.status), 0.15)}`,
                                                }}
                                            >
                                                <Typography sx={{ fontFamily: fontMono, fontSize: '0.48rem', color: palette.inkLight, textTransform: 'uppercase' }}>
                                                    {toDate(meeting.scheduledTime) ? MONTHS[toDate(meeting.scheduledTime).getMonth()] : '—'}
                                                </Typography>
                                                <Typography sx={{ fontFamily: fontDisplay, fontSize: '0.78rem', fontWeight: 700, color: toneFor(meeting.status), lineHeight: 1 }}>
                                                    {toDate(meeting.scheduledTime) ? toDate(meeting.scheduledTime).getDate() : '–'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: palette.ink }} noWrap>
                                                    {meeting.serviceCategory || meeting.requesterName || 'Meeting Request'}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.58rem', color: palette.inkLight }} noWrap>
                                                    {meeting.targetCompany || 'With Company'}
                                                    {meeting.scheduledTime ? ` · ${formatDateTime(meeting.scheduledTime)}` : ''}
                                                </Typography>
                                            </Box>
                                            <StatusPill status={meeting.status || 'pending'} />
                                        </LiquidRow>
                                    ))
                                )}
                            </Box>
                        </GlassPanel>
                    </Box>
                </Box>

                {/* ── latest invitations + meeting requests ────────────────── */}
                <Box
                    sx={{
                        display: 'grid', gap: 1.5,
                        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                        mb: 1.5,
                    }}
                >
                    <GlassPanel accent={palette.teal} delay={0.05}>
                        <PanelHead
                            icon={<InboxIcon />}
                            title="Latest Invitations"
                            accent={palette.teal}
                            chip={receivedInvitations.length > 0 ? `${receivedInvitations.length} recent` : 'none yet'}
                            action="View All"
                            onAction={() => navigate('/dashboard/companies')}
                            dense
                        />
                        <Box sx={{ flex: 1, maxHeight: 220, overflowY: 'auto', pr: 0.5, mr: -0.5 }}>
                            {loading ? (
                                Array.from({ length: 2 }).map((_, i) => (
                                    <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 0.6, borderRadius: '12px', bgcolor: palette.hairline }} />
                                ))
                            ) : receivedInvitations.length === 0 ? (
                                <EmptyState icon={<InboxIcon />} text="No invitations yet" />
                            ) : (
                                receivedInvitations.map((inv, i) => {
                                    const canAct = inv.status === 'pending' && inv._isReceiver;
                                    const otherName = inv._isReceiver ? inv.companyName : inv.targetCompanyName;
                                    return (
                                        <LiquidRow key={inv.id} accent={toneFor(inv.status)} index={i}>
                                            <Box
                                                sx={{
                                                    width: 32, height: 32, flexShrink: 0, borderRadius: '9px',
                                                    display: 'grid', placeItems: 'center', color: '#fff',
                                                    background: `linear-gradient(150deg, ${rgba(toneFor(inv.status), 0.9)}, ${rgba(toneFor(inv.status), 0.6)})`,
                                                    fontFamily: fontDisplay, fontWeight: 700, fontSize: '0.85rem',
                                                }}
                                            >
                                                {(otherName || 'C').charAt(0).toUpperCase()}
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: palette.ink }} noWrap>
                                                    {otherName || 'A company'}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.58rem', color: palette.inkLight }} noWrap>
                                                    {inv._isReceiver ? 'sent you an intro' : 'you sent an intro'} · {formatRelative(inv.updatedAt || inv.createdAt)}
                                                </Typography>
                                            </Box>
                                            {canAct ? (
                                                <>
                                                    <IconButton
                                                        size="small"
                                                        disabled={processingFloatingItem}
                                                        onClick={() => handleAcceptInvitationDirect(inv)}
                                                        sx={{ width: 26, height: 26, borderRadius: '8px', color: palette.pine, background: rgba(palette.pine, 0.1) }}
                                                    >
                                                        <CheckIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        disabled={processingFloatingItem}
                                                        onClick={() => handleDeclineInvitationDirect(inv)}
                                                        sx={{ width: 26, height: 26, borderRadius: '8px', color: palette.danger, background: rgba(palette.danger, 0.1) }}
                                                    >
                                                        <CloseIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                </>
                                            ) : (
                                                <StatusPill status={inv.status || 'pending'} />
                                            )}
                                        </LiquidRow>
                                    );
                                })
                            )}
                        </Box>
                    </GlassPanel>

                    <GlassPanel accent={palette.rust} delay={0.1}>
                        <PanelHead
                            icon={<RequestPageIcon />}
                            title="Latest Meeting Requests"
                            accent={palette.rust}
                            chip={receivedMeetingRequests.length > 0 ? `${receivedMeetingRequests.length} recent` : 'none yet'}
                            action="View All"
                            onAction={() => navigate('/dashboard/meetings')}
                            dense
                        />
                        <Box sx={{ flex: 1, maxHeight: 220, overflowY: 'auto', pr: 0.5, mr: -0.5 }}>
                            {loading ? (
                                Array.from({ length: 2 }).map((_, i) => (
                                    <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 0.6, borderRadius: '12px', bgcolor: palette.hairline }} />
                                ))
                            ) : receivedMeetingRequests.length === 0 ? (
                                <EmptyState icon={<RequestPageIcon />} text="No meeting requests yet" />
                            ) : (
                                receivedMeetingRequests.map((meeting, i) => {
                                    const canAct = meeting.status === 'pending' && meeting._isReceiver;
                                    const otherName = meeting._isReceiver
                                        ? (meeting.requesterName || meeting.requesterCompany)
                                        : (meeting.targetCompany || meeting.targetName);
                                    return (
                                        <LiquidRow key={meeting.id} accent={toneFor(meeting.status)} index={i} onClick={() => navigate('/dashboard/meetings')}>
                                            <Box
                                                sx={{
                                                    width: 32, height: 32, flexShrink: 0, borderRadius: '9px',
                                                    display: 'grid', placeItems: 'center', color: '#fff',
                                                    background: `linear-gradient(150deg, ${rgba(toneFor(meeting.status), 0.9)}, ${rgba(toneFor(meeting.status), 0.6)})`,
                                                }}
                                            >
                                                <EventIcon sx={{ fontSize: 15 }} />
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: palette.ink }} noWrap>
                                                    {meeting.title || otherName || 'Meeting Request'}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.58rem', color: palette.inkLight }} noWrap>
                                                    {meeting._isReceiver ? 'from ' : 'to '}{otherName || 'a company'} · {formatRelative(meeting.updatedAt || meeting.createdAt)}
                                                </Typography>
                                            </Box>
                                            {canAct ? (
                                                <GlassPill label="Respond" color={palette.rust} filled size="sm" />
                                            ) : (
                                                <StatusPill status={meeting.status || 'pending'} />
                                            )}
                                        </LiquidRow>
                                    );
                                })
                            )}
                        </Box>
                    </GlassPanel>
                </Box>

                {/* ── companies / all users (tabbed) ────────────────────────────── */}
                <GlassPanel accent={palette.rust} delay={0.05} lift={false}>
                    <PanelHead
                        icon={<BusinessIcon />}
                        title={companiesTab === 0 ? 'Companies on Platform' : 'All Users'}
                        accent={palette.rust}
                        chip={companiesTab === 0 ? `${companies.length} available` : `${platformUsers.length} people`}
                        action="View All"
                        onAction={() => navigate('/dashboard/companies')}
                        dense
                    />

                    <Tabs
                        value={companiesTab}
                        onChange={(e, v) => setCompaniesTab(v)}
                        sx={{
                            minHeight: 34,
                            mb: 1.25,
                            '& .MuiTabs-flexContainer': { gap: 0.5 },
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                                fontFamily: fontBody,
                                fontSize: '0.74rem',
                                minHeight: 30,
                                py: 0.4,
                                px: 1.5,
                                borderRadius: '10px',
                                color: palette.inkLight,
                            },
                            '& .Mui-selected': {
                                color: '#fff !important',
                                background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                boxShadow: `0 4px 14px ${rgba(palette.rust, 0.3)}`,
                            },
                            '& .MuiTabs-indicator': { display: 'none' },
                        }}
                    >
                        <Tab icon={<BusinessIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="Companies" />
                        <Tab icon={<PeopleIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="All Users" />
                    </Tabs>

                    {companiesTab === 0 ? (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
                                gap: 1.25,
                                width: '100%',
                            }}
                        >
                            {loading ? (
                                Array.from({ length: 3 }).map((_, i) => (
                                    <Skeleton key={i} variant="rounded" height={172} sx={{ borderRadius: '20px', bgcolor: palette.hairline }} />
                                ))
                            ) : companies.length > 0 ? (
                                companies.slice(0, 6).map((company, index) => {
                                    const gradient = GRADIENT_PAIRS[index % GRADIENT_PAIRS.length];
                                    return (
                                        <ModernCompanyCard
                                            key={company.id || index}
                                            company={company}
                                            gradient={gradient}
                                            onClick={() => handleCompanyClick(company)}
                                            introStatus={sentIntros[company.id]?.status}
                                            onSendIntro={handleSendAiIntro}
                                        />
                                    );
                                })
                            ) : (
                                <Box sx={{ gridColumn: '1 / -1', py: 2 }}>
                                    <EmptyState icon={<BusinessIcon />} text="No other companies on the platform yet" />
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ maxHeight: 560, overflowY: 'auto', pr: 0.5, mr: -0.5 }}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
                                    gap: 1.25,
                                    width: '100%',
                                }}
                            >
                                {loading ? (
                                    Array.from({ length: 6 }).map((_, i) => (
                                        <Skeleton key={i} variant="rounded" height={172} sx={{ borderRadius: '20px', bgcolor: palette.hairline }} />
                                    ))
                                ) : platformUsers.length > 0 ? (
                                    platformUsers.map((user, index) => {
                                        const gradient = GRADIENT_PAIRS[(index + 2) % GRADIENT_PAIRS.length];
                                        return (
                                            <ModernUserCard
                                                key={user.id || index}
                                                user={user}
                                                company={companies.find((company) => String(company.id || company.companyId || '') === String(user.companyId || user.company?.id || user.company?.companyId || '')) || null}
                                                gradient={gradient}
                                                introStatus={sentUserIntros[user.uid || user.id]?.status}
                                                onSendIntro={handleSendUserIntro}
                                            />
                                        );
                                    })
                                ) : (
                                    <Box sx={{ gridColumn: '1 / -1', py: 2 }}>
                                        <EmptyState icon={<PeopleIcon />} text="No other users on the platform yet" />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </GlassPanel>
            </Container>

            <FloatingSupportButton onNavigate={navigate} />
            <FloatingInvitesPanel
                invitations={actionableInvitations}
                meetingRequests={actionableMeetingRequests}
                onAcceptInvitation={handleAcceptInvitationDirect}
                onDeclineInvitation={handleDeclineInvitationDirect}
                onNavigateMeetings={() => navigate('/dashboard/meetings')}
                processing={processingFloatingItem}
            />

            {/* ── Company Detail Dialog ────────────────────────────────────── */}
            <Dialog
                open={openCompanyDetailDialog}
                onClose={() => setOpenCompanyDetailDialog(false)}
                maxWidth="md"
                fullWidth
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: rgba(palette.ink, 0.28) } }, ...({ paper: {
                    sx: {
                        ...liquid(palette.rust, { radius: 24 }),
                        background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 60%, ${rgba(palette.rust, 0.06)})`,
                        maxHeight: '90vh',
                    },
                } }) }}
                
            >
                {selectedCompanyDetail && (
                    <>
                        <Box
                            sx={{
                                p: 2.25,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                borderBottom: `1px solid ${palette.hairlineSoft}`,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '14px',
                                        display: 'grid',
                                        placeItems: 'center',
                                        color: '#fff',
                                        background: `linear-gradient(150deg, ${rgba(COMPANY_TYPE_COLORS[selectedCompanyDetail.companyType] || palette.rust, 0.9)}, ${rgba(COMPANY_TYPE_COLORS[selectedCompanyDetail.companyType] || palette.rust, 0.6)})`,
                                        boxShadow: `0 4px 14px ${rgba(COMPANY_TYPE_COLORS[selectedCompanyDetail.companyType] || palette.rust, 0.3)}`,
                                        flexShrink: 0,
                                        fontFamily: fontDisplay,
                                        fontSize: '1.1rem',
                                        fontWeight: 700,
                                    }}
                                >
                                    {selectedCompanyDetail.companyName?.charAt(0) || 'C'}
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                        <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.1rem', color: palette.ink }}>
                                            {selectedCompanyDetail.companyName}
                                        </Typography>
                                        <IntroStatusPill status={selectedIntroStatus} />
                                    </Box>
                                    <Typography sx={{ color: palette.inkLight, fontSize: '0.72rem' }}>
                                        {selectedCompanyDetail.country || 'N/A'} • {(selectedCompanyDetail.companyType || 'Company').replace(/_/g, ' ')}
                                        {selectedCompanyDetail.isVerified && ' ✅'}
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton
                                onClick={() => setOpenCompanyDetailDialog(false)}
                                sx={{
                                    width: 32, height: 32, borderRadius: '10px', color: palette.inkSoft,
                                    bgcolor: rgba(palette.ink, 0.04),
                                    '&:hover': { bgcolor: rgba(palette.ink, 0.08), transform: 'rotate(90deg)' },
                                    transition: 'transform .3s ease, background .3s ease',
                                }}
                            >
                                <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Box>

                        <DialogContent sx={{ px: 2.25, py: 2 }}>
                            <Box sx={{ display: 'grid', gap: 1.75 }}>
                                {/* Locked notice while an intro is pending */}
                                {selectedIntroStatus === 'pending' && (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 1.5,
                                            ...glassWell(14),
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.25,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 30, height: 30, borderRadius: '9px', flexShrink: 0,
                                                display: 'grid', placeItems: 'center', color: '#fff',
                                                background: `linear-gradient(150deg, ${rgba(palette.amber, 0.9)}, ${rgba(palette.amber, 0.6)})`,
                                            }}
                                        >
                                            <LockIcon sx={{ fontSize: 15 }} />
                                        </Box>
                                        <Typography sx={{ fontSize: '0.76rem', color: palette.inkSoft }}>
                                            Your AI intro is awaiting a response. Messaging and scheduling unlock as soon as{' '}
                                            <strong>{selectedCompanyDetail.companyName}</strong> accepts.
                                        </Typography>
                                    </Paper>
                                )}

                                {/* About */}
                                <Paper elevation={0} sx={{ p: 2, ...glassWell(16) }}>
                                    <Typography sx={{ fontWeight: 600, color: palette.ink, mb: 0.75, fontSize: '0.78rem' }}>
                                        About
                                    </Typography>
                                    <Typography sx={{ color: palette.inkSoft, lineHeight: 1.7, fontSize: '0.82rem' }}>
                                        {selectedCompanyDetail.description || 'No description provided.'}
                                    </Typography>
                                </Paper>

                                {/* Details Grid */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                                    <DetailTile
                                        icon={<BusinessIcon />}
                                        color={palette.rust}
                                        label="Company Type"
                                        value={selectedCompanyDetail.companyType?.replace(/_/g, ' ') || 'N/A'}
                                    />
                                    <DetailTile
                                        icon={<LocationIcon />}
                                        color={palette.teal}
                                        label="Country"
                                        value={selectedCompanyDetail.country || 'N/A'}
                                    />
                                    <DetailTile
                                        icon={<EmailIcon />}
                                        color={palette.teal}
                                        label="Email"
                                        value={selectedCompanyDetail.email || 'N/A'}
                                    />
                                    <DetailTile
                                        icon={<PhoneIcon />}
                                        color={palette.amber}
                                        label="Phone"
                                        value={selectedCompanyDetail.dialCode ? `${selectedCompanyDetail.dialCode} ${selectedCompanyDetail.phone}` : selectedCompanyDetail.phone || 'N/A'}
                                    />
                                    {selectedCompanyDetail.website && (
                                        <DetailTile
                                            icon={<LanguageIcon />}
                                            color={palette.plum}
                                            label="Website"
                                            value={
                                                <a
                                                    href={safeExternalUrl(selectedCompanyDetail.website, { allowBlob: false }) || undefined}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: palette.rust, textDecoration: 'none', fontWeight: 600 }}
                                                >
                                                    {selectedCompanyDetail.website}
                                                </a>
                                            }
                                        />
                                    )}
                                    {selectedCompanyDetail.companySize && selectedCompanyDetail.companySize !== 'N/A' && (
                                        <DetailTile
                                            icon={<PeopleIcon />}
                                            color={palette.pine}
                                            label="Company Size"
                                            value={selectedCompanyDetail.companySize}
                                        />
                                    )}
                                </Box>

                                {/* Services */}
                                {selectedCompanyDetail.services && selectedCompanyDetail.services.length > 0 && (
                                    <Box>
                                        <Typography sx={{ fontWeight: 600, color: palette.ink, mb: 0.75, fontSize: '0.78rem' }}>
                                            Services
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                            {selectedCompanyDetail.services.map((service, idx) => (
                                                <GlassPill key={`service-${idx}`} label={service} color={palette.amber} />
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                {/* Regions */}
                                {selectedCompanyDetail.regions && selectedCompanyDetail.regions.length > 0 && (
                                    <Box>
                                        <Typography sx={{ fontWeight: 600, color: palette.ink, mb: 0.75, fontSize: '0.78rem' }}>
                                            Regions
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                            {selectedCompanyDetail.regions.map((region, idx) => (
                                                <GlassPill key={`region-${idx}`} label={region} color={palette.teal} />
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                {/* Connect With */}
                                {selectedCompanyDetail.connectWith && selectedCompanyDetail.connectWith.length > 0 && (
                                    <Box>
                                        <Typography sx={{ fontWeight: 600, color: palette.ink, mb: 0.75, fontSize: '0.78rem' }}>
                                            Connect With
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                            {selectedCompanyDetail.connectWith.map((item, idx) => (
                                                <GlassPill key={`connect-${idx}`} label={item} color={palette.pine} />
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                {/* LinkedIn */}
                                {selectedCompanyDetail.linkedin && (
                                    <Box>
                                        <Typography sx={{ fontWeight: 600, color: palette.ink, mb: 0.75, fontSize: '0.78rem' }}>
                                            Social
                                        </Typography>
                                        <DetailTile
                                            icon={<LinkedInIcon />}
                                            color="#0a66c2"
                                            label="LinkedIn"
                                            value={
                                                <a
                                                    href={safeExternalUrl(selectedCompanyDetail.linkedin, { allowBlob: false }) || undefined}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: palette.rust, textDecoration: 'none', fontWeight: 600 }}
                                                >
                                                    View Profile
                                                </a>
                                            }
                                        />
                                    </Box>
                                )}
                            </Box>
                        </DialogContent>

                        <Box
                            sx={{
                                px: 2.25,
                                py: 1.75,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 1,
                                borderTop: `1px solid ${palette.hairlineSoft}`,
                                background: rgba('#FFFFFF', 0.45),
                                backdropFilter: 'blur(8px)',
                                flexWrap: 'wrap',
                            }}
                        >
                            <Button
                                onClick={() => setOpenCompanyDetailDialog(false)}
                                sx={{ textTransform: 'none', fontWeight: 600, color: palette.inkSoft, borderRadius: '10px', px: 1.75, fontSize: '0.78rem' }}
                            >
                                Close
                            </Button>
                            <GlassSolidButton
                                label="View Profile"
                                color={palette.rust}
                                icon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                                onClick={() => handleNavigateToCompanyProfile(selectedCompanyDetail)}
                            />

                            {/*
                                Gate: Message + Schedule only unlock once the AI intro is
                                accepted. If nothing's been sent yet, offer "Send AI Intro"
                                in their place; if pending, show a disabled "Pending" state.
                            */}
                            {selectedIntroStatus === 'accepted' ? (
                                <>
                                    <GlassSolidButton
                                        label="Message"
                                        color={palette.amber}
                                        variant="soft"
                                        icon={<ChatIcon sx={{ fontSize: 14 }} />}
                                        onClick={() => {
                                            toast('Chat with ' + selectedCompanyDetail.companyName, { icon: '💬' });
                                            setOpenCompanyDetailDialog(false);
                                            navigate('/dashboard/messages', {
                                                state: { company: selectedCompanyDetail, openChat: true },
                                            });
                                        }}
                                    />
                                    <GlassSolidButton
                                        label="Schedule Meeting"
                                        color={palette.pine}
                                        icon={<EventIcon sx={{ fontSize: 14 }} />}
                                        onClick={() => {
                                            setOpenCompanyDetailDialog(false);
                                            navigate('/dashboard/meetings/schedule', { state: { company: selectedCompanyDetail } });
                                        }}
                                    />
                                </>
                            ) : selectedIntroStatus === 'pending' ? (
                                <>
                                    <Tooltip title="Available once they accept your AI intro">
                                        <span>
                                            <GlassSolidButton
                                                label="Message"
                                                color={palette.inkLight}
                                                variant="soft"
                                                icon={<LockIcon sx={{ fontSize: 13 }} />}
                                                disabled
                                            />
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Available once they accept your AI intro">
                                        <span>
                                            <GlassSolidButton
                                                label="Schedule Meeting"
                                                color={palette.inkLight}
                                                icon={<LockIcon sx={{ fontSize: 13 }} />}
                                                disabled
                                            />
                                        </span>
                                    </Tooltip>
                                </>
                            ) : (
                                <GlassSolidButton
                                    label={sendingIntro ? 'Sending…' : 'Send AI Intro'}
                                    color={palette.plum}
                                    icon={<AiIcon sx={{ fontSize: 14 }} />}
                                    disabled={sendingIntro}
                                    onClick={() => handleSendAiIntro(selectedCompanyDetail)}
                                />
                            )}
                        </Box>
                    </>
                )}
            </Dialog>

            {/* ── Notifications Dialog ──────────────────────────────────────── */}
            <Dialog
                open={openNotificationDialog}
                onClose={() => setOpenNotificationDialog(false)}
                maxWidth="sm"
                fullWidth
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: rgba(palette.ink, 0.28) } }, ...({ paper: {
                    sx: {
                        ...liquid(palette.amber, { radius: 24 }),
                        background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 60%, ${rgba(palette.amber, 0.06)})`,
                        maxHeight: '80vh',
                    },
                } }) }}
                
            >
                <Box
                    sx={{
                        p: 2.25,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${palette.hairlineSoft}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '12px',
                                display: 'grid',
                                placeItems: 'center',
                                color: '#fff',
                                background: `linear-gradient(150deg, ${rgba(palette.amber, 0.9)}, ${rgba(palette.amber, 0.6)})`,
                                boxShadow: `0 4px 14px ${rgba(palette.amber, 0.3)}`,
                            }}
                        >
                            <NotificationIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.1rem', color: palette.ink }}>
                                Notifications
                            </Typography>
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.72rem' }}>
                                {pendingNotifications.length > 0 ? `${pendingNotifications.length} pending` : 'All caught up'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={() => setOpenNotificationDialog(false)}
                        sx={{
                            width: 32, height: 32, borderRadius: '10px', color: palette.inkSoft,
                            bgcolor: rgba(palette.ink, 0.04),
                            '&:hover': { bgcolor: rgba(palette.ink, 0.08), transform: 'rotate(90deg)' },
                            transition: 'transform .3s ease, background .3s ease',
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>

                <DialogContent sx={{ px: 2.25, py: 2 }}>
                    {pendingNotifications.length === 0 ? (
                        <EmptyState icon={<NotificationIcon />} text="Nothing pending — you're all caught up" />
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {pendingNotifications.map((notification) => (
                                <NotificationCard
                                    key={notification.id}
                                    notification={notification}
                                    onAccept={handleAcceptNotification}
                                    onDecline={handleDeclineNotification}
                                    onView={handleViewNotification}
                                    onChat={handleChatFromNotification}
                                />
                            ))}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <style>{`
                @keyframes tfPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%      { opacity: .45; transform: scale(.82); }
                }
                @keyframes tfRule {
                    0%, 100% { opacity: .7; transform: scaleX(1); }
                    50%      { opacity: 1;  transform: scaleX(1.35); }
                }
                @keyframes tfGrain {
                    0%   { transform: translate(0, 0); }
                    25%  { transform: translate(-2%, 1%); }
                    50%  { transform: translate(1%, -2%); }
                    75%  { transform: translate(-1%, -1%); }
                    100% { transform: translate(0, 0); }
                }
                @keyframes tfOrbFloat {
                    0%, 100% { transform: translate(0, 0) scale(1); }
                    50%      { transform: translate(-10px, -10px) scale(1.1); }
                }
                * { scrollbar-width: thin; scrollbar-color: ${rgba(palette.ink, 0.14)} transparent; }
                *::-webkit-scrollbar { width: 5px; height: 5px; }
                *::-webkit-scrollbar-track { background: transparent; }
                *::-webkit-scrollbar-thumb {
                    background: ${rgba(palette.ink, 0.12)};
                    border-radius: 999px;
                }
                *::-webkit-scrollbar-thumb:hover { background: ${rgba(palette.ink, 0.22)}; }
                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: .001ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: .001ms !important;
                    }
                }
            `}</style>
        </Box>
    );
};

export default CompanyDashboard;